import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { OdometerScanButton } from '../components/OdometerScanner';
import axios from 'axios';
import { statusClass, statusLabel } from '../utils/statusBadge';
import { toast } from 'sonner';
import { disconnectAppSocket, getAppSocket } from '../lib/wesSocket';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrencyAR, formatNumberAR, formatNumberARNoDecimals } from '../utils/currencyFormatter';
import { ComboItem, comboDesdeProducto, toggleComboItem } from '../lib/comboItems';
import { openPdf } from '../utils/pdfHelpers';
import CouponInput from '../components/CouponInput';
import VehicleCreateDialog from '../components/VehicleCreateDialog';
import ClientDataDialog from '../components/ClientDataDialog';
import {
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  PrinterIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  DocumentDuplicateIcon,
  PaperClipIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import ClientCombobox from '../components/ClientCombobox';
import ClientQuickPicks from '../components/ClientQuickPicks';
import VehicleCombobox from '../components/VehicleCombobox';
import ProductCombobox from '../components/ProductCombobox';
import RemitoPrintView from '../components/RemitoPrintView';
import ErrorBoundary from '../components/ErrorBoundary';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/ui/number-input';
import { DatePicker } from '../components/ui/date-picker';
import BankCombobox from '../components/BankCombobox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../components/ui/tooltip';
import { Tip } from '../components/ui/tip';
import { confirmDialog } from '../components/ConfirmDialog';
import { hoyLocal, toYMD } from '../utils/fechaLocal';
import {
  serviciosElegibles, serviciosExcluidos, esServicioExcluido, entraEnDescuentoDeServicios,
  separarPorDecision, esItemLibre, decisionManual, vincularConCatalogo, buscarEnCatalogo
} from '../utils/discountMatch';
import { DatePicker as DatePickerUI } from '../components/ui/date-picker';
import { PagoMixtoEditor } from '../components/PagoMixtoEditor';
import { useRetiro } from '../components/RetiroDialog';
import {
  LineaPago, METODO_MIXTO, LABEL_MIXTO, OPCION_MIXTO, esMixto, lineasDesdePagos, lineasIniciales,
  payloadPagos, repartirLineas, resumenPagos
} from '../utils/cobroMixto';

interface Remito {
  id: string;
  remitNumber: string;
  clientId: string;
  vehicleId?: string;
  clientName: string;
  clientIsPlaceholder?: boolean;
  clientPhone?: string | null;
  companyName?: string;
  vehicleInfo?: string;
  licensePlate?: string;
  type: 'service' | 'delivery' | 'warranty' | 'internal';
  serviceType?: string;
  observations?: string;
  status: 'draft' | 'issued' | 'delivered' | 'cancelled';
  totalQuantity: number;
  estimatedValue: number;
  issuedAt?: string;
  deliveredAt?: string;
  receivedBy?: string;
  receivedByDni?: string | null;
  invoicedAt?: string | null;
  paidAt?: string | null;
  createdAt: string;
  createdByName: string;
  relatedBudgetId?: string;
  relatedAppointmentId?: string;
  paymentMethod?: string;
  paymentInterval?: number;
  emailSent?: boolean;
  whatsappSent?: boolean;
  totalContado?: number;
  appliedDiscount?: any;
  appliedCoupon?: any;
  discountAmount?: number;
  /** Cobro mixto (paymentMethod 'mixto'): un renglón por medio, recargo solo sobre su parte */
  payments?: Array<{ id?: string; method: string; base: number; surchargePct: number; surcharge: number; amount: number; accountId?: string | null; paymentInterval?: number | null }>;
  attachments?: Array<{ id: string; filename: string; originalName: string; mimeType: string; size: number; uploadedAt: string; uploadedBy: string }>;
  items: Array<{
    name: string;
    description?: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    type?: string;
  }>;
}

interface Client {
  id: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  type: 'individual' | 'company';
  vehicles?: Vehicle[];
}

interface Vehicle {
  id: string;
  make?: string;
  brand?: string;
  model: string;
  year?: string;
  licensePlate: string;
  clientId?: string;
}

interface Budget {
  id: string;
  budgetNumber?: string;
  clientName: string;
  subject?: string;
  description?: string;
  totalAmount: number;
  status: string;
  paymentMethod?: string;
  items?: Array<{
    name: string;
    description?: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
}

interface Appointment {
  id: string;
  clientName: string;
  vehicleInfo?: string;
  serviceType: string;
  description: string;
  scheduledDate: string;
  status: string;
  items?: any[];
  services?: any[];
  products?: any[];
}

interface RemitoItem {
  productId?: string;
  name: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  type?: string;
  // Combo ("Detailing T2"): inclusiones tildadas para esta visita
  comboItems?: ComboItem[];
}

// Función para convertir códigos de medio de pago a texto legible
function getPaymentMethodLabel(paymentMethod: string): string {
  const labels: { [key: string]: string } = {
    'efectivo': 'Efectivo',
    'debito': 'Débito',
    'transferencia': 'Transferencia',
    'cheque': 'Cheque',
    'tarjeta_3': 'Tarjeta de crédito en 3 pagos',
    'tarjeta_6': 'Tarjeta de crédito en 6 pagos',
    'cuenta_corriente': 'Cuenta Corriente',
    'mixto': LABEL_MIXTO
  };
  return labels[paymentMethod] || paymentMethod;
}

interface Product {
  id: string;
  name: string;
  description?: string;
  category: string;
  sku?: string;
  salePrice: number;
  isService?: boolean;
  duration?: number;
}

const Remitos: React.FC = () => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isCreatePage = location.pathname === '/remitos/new';
  const [remitos, setRemitos] = useState<Remito[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [processingActions, setProcessingActions] = useState<Set<string>>(new Set());
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showObservationsModal, setShowObservationsModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingRemito, setEditingRemito] = useState<Remito | null>(null);
  const [viewingRemito, setViewingRemito] = useState<Remito | null>(null);
  const { editarRetiroRemito, dialogoRetiro } = useRetiro();
  // Completar o corregir quién retiró el vehículo (v12.6.4)
  const cargarQuienRetiro = async (r: Remito) => {
    const datos = await editarRetiroRemito(r.id, [r.remitNumber, (r as any).clientName, (r as any).licensePlate].filter(Boolean).join(' · '));
    if (!datos) return;
    const cambio = { receivedBy: datos.nombre, receivedByDni: datos.dni, deliveredAt: r.deliveredAt || new Date().toISOString() };
    setViewingRemito((prev: any) => (prev && prev.id === r.id ? { ...prev, ...cambio } : prev));
    setRemitos((prev: any[]) => prev.map((x) => (x.id === r.id ? { ...x, ...cambio } : x)));
  };
  const [viewClientVehicles, setViewClientVehicles] = useState<any[]>([]);
  const [remitoKmInput, setRemitoKmInput] = useState('');
  const [editingRemitoKm, setEditingRemitoKm] = useState(false);
  const [savingRemitoKm, setSavingRemitoKm] = useState(false);
  const [remitoVehicleId, setRemitoVehicleId] = useState('');
  const [editingRemitoVehicle, setEditingRemitoVehicle] = useState(false);
  const [savingRemitoVehicle, setSavingRemitoVehicle] = useState(false);
  const [printingRemito, setPrintingRemito] = useState<Remito | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedRemitoForPayment, setSelectedRemitoForPayment] = useState<Remito | null>(null);
  const [reopenTarget, setReopenTarget] = useState<Remito | null>(null);
  const [deleteAttachmentTarget, setDeleteAttachmentTarget] = useState<{ remitoId: string; attachmentId: string; name: string } | null>(null);
  const [deletingAttachment, setDeletingAttachment] = useState(false);

  const confirmDeleteAttachment = async () => {
    if (!deleteAttachmentTarget) return;
    setDeletingAttachment(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`/api/remitos/${deleteAttachmentTarget.remitoId}/attachments/${deleteAttachmentTarget.attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Archivo eliminado');
      setViewingRemito((prev: any) => prev ? { ...prev, attachments: res.data.attachments } : prev);
      setDeleteAttachmentTarget(null);
      fetchRemitos();
    } catch (err: any) {
      console.error('Error eliminando adjunto:', err.response?.status, err.response?.data, err);
      const msg = err.response?.data?.error || err.response?.statusText || err.message || 'Error desconocido';
      toast.error(`Error al eliminar archivo: ${msg}`);
    } finally {
      setDeletingAttachment(false);
    }
  };
  const [items, setItems] = useState<RemitoItem[]>([]);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [vehiclePrefillPlate, setVehiclePrefillPlate] = useState('');
  const [focusedItemIndex, setFocusedItemIndex] = useState<number | null>(null);
  const productSearchContainerRef = useRef<HTMLDivElement | null>(null);
  const nameInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const qtyInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const priceInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const itemRowRefs = useRef<Record<number, HTMLTableRowElement | null>>({});
  const prevItemsLengthRef = useRef<number>(0);

  // Estados para facturación
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedRemitoForInvoice, setSelectedRemitoForInvoice] = useState<Remito | null>(null);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);

  // Estados para descuentos
  const [customDiscounts, setCustomDiscounts] = useState<any[]>([]);
  const [selectedDiscount, setSelectedDiscount] = useState<string>('');
  const [appliedDiscount, setAppliedDiscount] = useState<any>(null);
  // Renglones enganchados al catálogo (tipo real; "Alineacion furgon" escrito a
  // mano = el servicio del catálogo). Los que no enganchan son ítems libres.
  const itemsVinculados = useMemo(() => vincularConCatalogo(items, products), [items, products]);
  const refreshTimeoutRef = useRef<number | null>(null);
  const fetchRemitosRef = useRef<null | ((options?: { silent?: boolean }) => void)>(null);

  // Estados para cupones
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);

  // Estados para selección múltiple
  const [selectedRemitos, setSelectedRemitos] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState<string | null>(null);
  
  // Estado para el rol del usuario (asumiendo que es admin)
  const [userRole, setUserRole] = useState<'admin' | 'user'>('admin');

  // Estados para métodos de pago
  const [paymentOptions, setPaymentOptions] = useState<Array<{value: string, label: string, surcharge?: number}>>([]);
  // Recargo por medio (para la vista previa del cobro mixto; el server recalcula al guardar)
  const recargosPorMetodo = useMemo(
    () => Object.fromEntries(paymentOptions.map((o) => [o.value, Number(o.surcharge) || 0])) as Record<string, number>,
    [paymentOptions]
  );
  // Renglones del cobro mixto: form de alta/edición, modal de cambio de medio y "emitir sin medio"
  const [pagosMixtos, setPagosMixtos] = useState<LineaPago[]>([]);
  const [pagosMixtosModal, setPagosMixtosModal] = useState<LineaPago[]>([]);
  const [pagosMixtosEmitir, setPagosMixtosEmitir] = useState<LineaPago[]>([]);
  // Base que se reparte en un cobro mixto: ítems menos descuento (sin recargo)
  const baseCobrableDe = (r: any): number => {
    const itemsSum = Array.isArray(r?.items) && r.items.length
      ? r.items.reduce((s: number, it: any) => s + Number(it.totalPrice || 0), 0)
      : Number(r?.estimatedValue || 0);
    return Math.max(0, itemsSum - Number(r?.discountAmount || 0));
  };
  const [ccIntervalOptions, setCcIntervalOptions] = useState<Array<{value: number, label: string}>>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  // Cuentas propias (para indicar a qué cuenta entra la plata en transferencia/débito/QR)
  const [financialAccounts, setFinancialAccounts] = useState<Array<{ id: string; name: string; kind?: string; isDefault?: number }>>([]);
  // Datos del cheque cuando el medio de pago es 'cheque' (crea el cheque en cartera)
  const [chequeNumero, setChequeNumero] = useState('');
  const [chequeBanco, setChequeBanco] = useState('');
  const [chequeVencimiento, setChequeVencimiento] = useState('');
  // Cliente "por completar" (creado solo con la patente): al cobrar se piden
  // los datos minimos antes de emitir. Usa el dialog compartido (ClientDataDialog),
  // que ademas permite vincular el "Sin datos" a un cliente ya cargado (fusion).
  const [cdClientId, setCdClientId] = useState<string | null>(null);
  const [cdPostCobro, setCdPostCobro] = useState(false);
  const cdResolverRef = useRef<((ok: boolean) => void) | null>(null);

  const ensureClientData = (clientId: string): Promise<boolean> => {
    const cli = (clients || []).find((c: any) => c.id === clientId);
    if (!cli || Number((cli as any).isPlaceholder) !== 1) return Promise.resolve(true);
    setCdPostCobro(false);
    setCdClientId(clientId);
    return new Promise((resolve) => { cdResolverRef.current = resolve; });
  };

  // Completar los datos de un cliente "Sin datos" despues del cobro (desde la lista)
  const completarDatosCliente = (remito: any) => {
    cdResolverRef.current = null;
    setCdPostCobro(true);
    setCdClientId(remito.clientId);
  };

  // El flujo "Cliente sin datos (solo patente)" vive en ClientQuickPicks
  const [selectedPaymentInterval, setSelectedPaymentInterval] = useState<number | null>(null);
  const [calculatedPrice, setCalculatedPrice] = useState<number>(0);
  const [priceSurcharge, setPriceSurcharge] = useState<number>(0);
  const [modalCalculatedPrice, setModalCalculatedPrice] = useState<number>(0);
  const [modalPriceSurcharge, setModalPriceSurcharge] = useState<number>(0);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [remitoFilter, setRemitoFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Llegando desde una factura: mostrar solo los remitos de esa factura (por id)
  const [remitoIdsFilter, setRemitoIdsFilter] = useState<string[]>([]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q');
    if (query && query !== searchTerm) {
      setSearchTerm(query);
    }
    // Permite llegar filtrado desde otras pantallas (/remitos?filter=draft)
    const filter = params.get('filter');
    if (filter) {
      setRemitoFilter(filter);
    }
    // /remitos?remitoIds=id1,id2 (desde el botón "Ver remito" de una factura)
    const ids = params.get('remitoIds');
    setRemitoIdsFilter(ids ? ids.split(',').map((s) => s.trim()).filter(Boolean) : []);
  }, [location.search, searchTerm]);

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const { register, handleSubmit, reset, watch, setValue, formState: { errors }, control } = useForm();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const todayStr = hoyLocal();
  const selectedClientId = watch('clientId');
  const selectedBudgetId = watch('relatedBudgetId');
  const selectedAppointmentId = watch('relatedAppointmentId');

  // Filtrar vehículos por cliente seleccionado
  const filteredVehicles = (vehicles || []).filter(vehicle => 
    !selectedClientId || vehicle.clientId === selectedClientId
  );

  useEffect(() => {
    // Verificar si el usuario está autenticado antes de hacer llamadas
    const token = localStorage.getItem('token');
    if (token) {
    fetchRemitos();
    fetchClients();
    fetchProducts();
      fetchPaymentOptions();
      axios.get('/api/financial-accounts').then(r => setFinancialAccounts(r.data.accounts || [])).catch(() => {});
    // Cargar todos los presupuestos y citas inicialmente
    fetchBudgets();
    fetchAppointments();
    fetchCustomDiscounts();
    }
  }, []);

  useEffect(() => {
    const socket = getAppSocket();
    const scheduleSilentRefresh = (payload?: any) => {
      const path = payload?.path ? String(payload.path) : '';
      if (path && !(path.startsWith('/api/remitos') || path.startsWith('/api/current-accounts'))) {
        return;
      }

      if (refreshTimeoutRef.current) window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = window.setTimeout(() => {
        fetchRemitosRef.current?.({ silent: true });
      }, 400);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('cash:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('cash:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
      disconnectAppSocket();
    };
  }, []);

  // Manejo de errores global para evitar problemas de navegación
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (event.error && event.error.message && event.error.message.includes('Route')) {
        event.preventDefault();
        console.warn('Error de navegación interceptado:', event.error);
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason && event.reason.message && event.reason.message.includes('Route')) {
        event.preventDefault();
        console.warn('Error de navegación interceptado:', event.reason);
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Manejo seguro del cierre del modal
  const handleCloseModal = () => {
    try {
      if (isCreatePage) {
        navigate('/remitos');
        return;
      }

      // Prevenir cualquier navegación durante el cierre
      const currentPath = window.location.pathname;
      
      // Primero limpiar estados
      setEditingRemito(null);
      setItems([]);
      setSelectedPaymentMethod('');
      setSelectedPaymentInterval(null);
      setCalculatedPrice(0);
      setSelectedDiscount('');
      setAppliedDiscount(null);
      setPriceSurcharge(0);
      setPagosMixtos([]);
      
      // Luego resetear formulario
      reset({
        clientId: '',
        vehicleId: '',
        type: 'service',
        serviceType: '',
        estimatedValue: '',
        observations: '',
        relatedBudgetId: '',
        relatedAppointmentId: '',
        paymentMethod: '',
        paymentInterval: '',
        mileage: ''
      });

      // Finalmente cerrar modal con delay para evitar conflictos
      setTimeout(() => {
        setShowModal(false);
        // Verificar que no se haya cambiado la ruta
        if (window.location.pathname !== currentPath) {
          console.warn('Ruta cambiada durante el cierre del modal, restaurando...');
          window.history.pushState(null, '', currentPath);
        }
      }, 100);
    } catch (error) {
      console.error('Error cerrando modal:', error);
      // Fallback: solo cerrar el modal
      setShowModal(false);
    }
  };

  // Manejo de escape para cerrar modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showModal) {
        handleCloseModal();
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showModal]);

  // Atajo de teclado para abrir el modal de nuevo remito (N)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      if (
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        target?.isContentEditable
      ) {
        return;
      }
      if (event.key.toLowerCase() === 'n') {
        event.preventDefault();
        navigate('/remitos/new');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  useEffect(() => {
    if (!isCreatePage) return;
    setEditingRemito(null);
    setItems([]);
    setSelectedPaymentMethod('');
    setSelectedPaymentInterval(null);
    setCalculatedPrice(0);
    setSelectedDiscount('');
    setAppliedDiscount(null);
    setPriceSurcharge(0);
    reset({
      clientId: '',
      vehicleId: '',
      type: 'service',
      serviceType: '',
      estimatedValue: '',
      observations: '',
      relatedBudgetId: '',
      relatedAppointmentId: '',
      paymentMethod: '',
      paymentInterval: '',
      mileage: ''
    });
  }, [isCreatePage, reset]);

  // Prevenir navegación no deseada
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (showModal) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      if (showModal) {
        event.preventDefault();
        // Restaurar la ruta actual
        window.history.pushState(null, '', '/remitos');
      }
    };

    if (showModal) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopState);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [showModal]);

  // Filtrar presupuestos y citas cuando cambia el cliente seleccionado
  useEffect(() => {
    if (selectedClientId) {
      fetchBudgets(selectedClientId);
      fetchAppointments(selectedClientId);
      // Limpiar selecciones previas
      setValue('relatedBudgetId', '');
      setValue('relatedAppointmentId', '');
    } else {
      // Si no hay cliente seleccionado, cargar todos
      fetchBudgets();
      fetchAppointments();
    }
  }, [selectedClientId, setValue]);

  // Cortesía por reseña de Google del cliente seleccionado (aviso en el form)
  const [clientCourtesy, setClientCourtesy] = useState<any>(null);
  useEffect(() => {
    if (!selectedClientId || selectedClientId === 'consumidor-final') {
      setClientCourtesy(null);
      return;
    }
    axios.get(`/api/reviews/courtesy/${selectedClientId}`)
      .then((r) => setClientCourtesy(r.data?.courtesy || null))
      .catch(() => setClientCourtesy(null));
  }, [selectedClientId]);

  useEffect(() => {
    // Al cambiar el cliente, limpiar el vehículo solo si no pertenece al nuevo cliente.
    // El flujo "Cliente sin datos (solo patente)" setea cliente y vehículo juntos:
    // limpiar siempre pisaba esa selección.
    if (selectedClientId) {
      const cli: any = clients.find((c: any) => c.id === selectedClientId);
      const currentVehicleId = watch('vehicleId');
      const belongs = currentVehicleId && (cli?.vehicles || []).some((v: any) => v.id === currentVehicleId);
      if (!belongs) {
        setValue('vehicleId', '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, setValue]);

  // Auto-completar datos cuando se selecciona un presupuesto
  useEffect(() => {
    if (selectedBudgetId) {
      const budget = (budgets || []).find(b => b.id === selectedBudgetId);
      if (budget) {
        setValue('estimatedValue', budget.totalAmount);
        setValue('type', 'service');
        // Cargar items del presupuesto
        if (budget.items && budget.items.length > 0) {
          setItems(budget.items);
        }
      }
    }
  }, [selectedBudgetId, budgets, setValue]);

  // Auto-completar datos cuando se selecciona una cita
  useEffect(() => {
    if (selectedAppointmentId) {
      const appointment = (appointments || []).find(a => a.id === selectedAppointmentId);
      if (appointment) {
        setValue('serviceType', appointment.serviceType);
        setValue('type', 'service');
      }
    }
  }, [selectedAppointmentId, appointments, setValue]);

  // Funciones para manejar items
  const addItem = () => {
    const updated = [...items, {
      productId: '',
      name: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      totalPrice: 0,
      type: 'product' // Por defecto, se actualizará cuando se seleccione un producto
    }];
    setItems(updated);
    setFocusedItemIndex(updated.length - 1);
  };

  const removeItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    setItems(updated);
    setFocusedItemIndex(updated.length > 0 ? updated.length - 1 : null);
    setValue('estimatedValue', updated.reduce((sum, item) => sum + (item.totalPrice || 0), 0));
  };

  const handleItemChange = (index: number, field: keyof RemitoItem, value: any) => {
    const newItems = [...items];
    
    // No permitir modificar el precio unitario
    if (field === 'unitPrice') {
      return;
    }
    
    // Preservar el campo type cuando se actualiza un item
    const currentItem = newItems[index];
    newItems[index] = { 
      ...currentItem, 
      [field]: value,
      type: currentItem.type || 'product' // Mantener el tipo existente o usar 'product' por defecto
    };
    
    // Calcular totalPrice solo si cambia la cantidad
    if (field === 'quantity') {
      newItems[index].totalPrice = (newItems[index].quantity || 0) * (newItems[index].unitPrice || 0);
    }
    
    setItems(newItems);
    
    // Actualizar valor estimado total
    const total = newItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    setValue('estimatedValue', total);
  };

  // Tildes del combo: destildar lo que NO se hizo en esta visita (queda
  // guardado en el item y el PDF/libreta muestran solo lo tildado)
  const toggleCombo = (index: number, comboIndex: number) => {
    const updated = [...items];
    const item = updated[index];
    if (!item?.comboItems) return;
    updated[index] = { ...item, comboItems: toggleComboItem(item.comboItems, comboIndex) };
    setItems(updated);
  };

  const comboChecklist = (item: RemitoItem, index: number) => (
    (item.comboItems && item.comboItems.length > 0) ? (
      <div className="mt-1.5 space-y-0.5">
        {item.comboItems.map((c, i) => (
          <label key={i} className="flex cursor-pointer items-center gap-2 text-[12.5px] text-muted-foreground">
            <input
              type="checkbox"
              checked={c.incluido}
              onChange={() => toggleCombo(index, i)}
              className="h-3.5 w-3.5 accent-foreground"
            />
            <span className={c.incluido ? '' : 'line-through opacity-60'}>{c.label}</span>
          </label>
        ))}
      </div>
    ) : null
  );

  // Casilla del ítem libre: tildada = lleva el descuento; destildada = vuelve
  // a la regla (un libre no lleva descuento)
  const toggleDescuentoLibre = (index: number, aplicar: boolean) => {
    const updated = [...items];
    const { applyDiscount: _prev, ...resto } = updated[index] as any;
    updated[index] = aplicar ? { ...resto, applyDiscount: true } : resto;
    setItems(updated);
  };

  // Debajo del nombre: si el renglón lleva el descuento; en los ítems libres
  // (sin artículo del catálogo), la casilla para aplicárselo o quitárselo
  const marcaDescuento = (index: number) => {
    if (!appliedDiscount) return null;
    const it = itemsVinculados[index];
    if (!it) return null;
    if (esItemLibre(it)) {
      return (
        <label className="mt-1 flex w-fit cursor-pointer items-center gap-2 text-[12px] text-muted-foreground">
          <input
            type="checkbox"
            checked={decisionManual(it) === true}
            onChange={(e) => toggleDescuentoLibre(index, e.target.checked)}
            className="h-3.5 w-3.5 accent-foreground"
          />
          <span>Aplicar descuento {appliedDiscount.name} (ítem libre)</span>
        </label>
      );
    }
    return itemsEnDescuento(appliedDiscount, itemsVinculados).includes(it)
      ? <p className="mt-1 text-[11px] text-green-600 dark:text-green-400">Con descuento {appliedDiscount.name}</p>
      : null;
  };

  const addItemFromProduct = (product: any) => {
    const isService = Boolean(product.isService) || String(product.category || '').toLowerCase() === 'service';
    const unitPrice = Number(product.sellingPrice ?? product.salePrice ?? 0);
    const newItem: RemitoItem = {
      productId: product.id,
      name: product.name,
      description: product.name,
      quantity: 1,
      unitPrice,
      totalPrice: unitPrice,
      type: isService ? 'service' : 'product',
      comboItems: comboDesdeProducto(product)
    };

    const updated = [...items, newItem];
    setItems(updated);
    setFocusedItemIndex(updated.length - 1);
    setValue('estimatedValue', updated.reduce((sum, item) => sum + (item.totalPrice || 0), 0));
  };

  const addManualItem = (name: string) => {
    const label = String(name || '').trim();
    if (!label) return;
    // Escrito a mano pero ya existe en el catálogo (sin mirar tildes ni
    // mayúsculas): cargar el artículo, no un ítem libre
    const delCatalogo = buscarEnCatalogo({ name: label }, products);
    if (delCatalogo) { addItemFromProduct(delCatalogo); return; }
    const updated = [
      ...items,
      {
        productId: '',
        name: label,
        description: label,
        quantity: 1,
        unitPrice: 0,
        totalPrice: 0,
        type: 'service'
      } as RemitoItem
    ];
    setItems(updated);
    setFocusedItemIndex(updated.length - 1);
    setValue('estimatedValue', updated.reduce((sum, item) => sum + (item.totalPrice || 0), 0));
  };

  const focusProductSearchInput = () => {
    const input = productSearchContainerRef.current?.querySelector('input');
    if (!input) return;
    input.focus();
    input.select?.();
  };

  useEffect(() => {
    if (!isCreatePage) return;

    const handleItemShortcuts = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const target = e.target as HTMLElement | null;
      const tag = target?.tagName || '';
      const isTypingTarget = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!target?.isContentEditable;
      const inputTarget = target as HTMLInputElement | null;
      const isItemFieldTarget = Boolean(inputTarget?.dataset?.itemField === 'true');
      const currentCol = inputTarget?.dataset?.itemCol || 'qty';

      if (isItemFieldTarget && e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        inputTarget?.blur();
        return;
      }

      if (items.length > 0 && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        const baseIndex = focusedItemIndex !== null ? focusedItemIndex : items.length - 1;
        const nextIndex = e.key === 'ArrowUp'
          ? Math.max(0, baseIndex - 1)
          : Math.min(items.length - 1, baseIndex + 1);

        e.preventDefault();
        e.stopPropagation();
        setFocusedItemIndex(nextIndex);

        const focusByCol = () => {
          if (currentCol === 'name') return nameInputRefs.current[nextIndex];
          if (currentCol === 'price') return priceInputRefs.current[nextIndex];
          return qtyInputRefs.current[nextIndex];
        };

        const targetInput = isItemFieldTarget ? focusByCol() : null;
        if (targetInput) {
          targetInput.focus();
          targetInput.select?.();
        } else {
          itemRowRefs.current[nextIndex]?.focus();
        }
        return;
      }

      if (isTypingTarget) return;

      if (e.key === '.' || e.code === 'Period' || e.code === 'NumpadDecimal') {
        e.preventDefault();
        e.stopPropagation();
        focusProductSearchInput();
        return;
      }

      if (!items.length) return;

      const targetIndex = focusedItemIndex !== null ? focusedItemIndex : items.length - 1;
      const qtyInput = qtyInputRefs.current[targetIndex];
      const priceInput = priceInputRefs.current[targetIndex];

      if (e.key === '*' || e.code === 'NumpadMultiply') {
        e.preventDefault();
        e.stopPropagation();
        qtyInput?.focus();
        qtyInput?.select?.();
        return;
      }

      if (e.key === '/' || e.code === 'NumpadDivide') {
        e.preventDefault();
        e.stopPropagation();
        priceInput?.focus();
        priceInput?.select?.();
        return;
      }

      if (e.key === ',' || e.code === 'Comma') {
        e.preventDefault();
        e.stopPropagation();
        const updated = items.filter((_, i) => i !== targetIndex);
        setItems(updated);
        setFocusedItemIndex(updated.length > 0 ? updated.length - 1 : null);
        setValue('estimatedValue', updated.reduce((sum, item) => sum + (item.totalPrice || 0), 0));
      }
    };

    window.addEventListener('keydown', handleItemShortcuts);
    return () => window.removeEventListener('keydown', handleItemShortcuts);
  }, [isCreatePage, items, focusedItemIndex, setValue]);

  useEffect(() => {
    if (!isCreatePage) return;
    const prevLength = prevItemsLengthRef.current;
    prevItemsLengthRef.current = items.length;

    if (items.length === 0 || focusedItemIndex === null) return;
    if (items.length === prevLength) return;

    requestAnimationFrame(() => {
      const searchInput = productSearchContainerRef.current?.querySelector('input') as HTMLInputElement | null;
      if (searchInput && document.activeElement === searchInput) {
        searchInput.blur();
      }
      const row = itemRowRefs.current[Math.min(focusedItemIndex, items.length - 1)];
      row?.focus();
    });
  }, [isCreatePage, items.length, focusedItemIndex]);


  // Función para cargar descuentos personalizados
  const fetchCustomDiscounts = async () => {
    try {
      const response = await axios.get('/api/custom-discounts');
      setCustomDiscounts(response.data);
    } catch (error) {
      console.error('Error cargando descuentos personalizados:', error);
    }
  };

  const isDiscountSelectable = (discount: any) => {
    if (!discount.enabled) return false;
    const today = hoyLocal();
    if (discount.start_date && discount.start_date > today) return false;
    if (discount.end_date && discount.end_date < today) return false;
    if (discount.max_uses && discount.current_uses >= discount.max_uses) return false;
    const eligibleClients = Array.isArray(discount.eligible_clients) ? discount.eligible_clients : [];
    if (eligibleClients.length > 0 && (!selectedClientId || !eligibleClients.includes(selectedClientId))) {
      return false;
    }
    if (discount.min_amount) {
      const subtotal = items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
      if (subtotal < discount.min_amount) return false;
    }
    return true;
  };

  // Función para manejar la selección de descuentos
  const handleDiscountChange = (discountId: string) => {
    setSelectedDiscount(discountId);
    if (discountId) {
      const discount = customDiscounts.find(d =>
        d.id === discountId ||
        d.id === parseInt(discountId) ||
        String(d.id) === discountId
      );
      setAppliedDiscount(discount);
    } else {
      setAppliedDiscount(null);
    }
  };

  // Auto-aplicar el descuento específico del cliente al elegirlo (editable: se
  // puede cambiar o quitar). Solo al crear, una vez por cliente. Si el cliente
  // tiene varios, aplica el de mayor prioridad (a igualdad, mayor descuento).
  const lastAutoDiscClientRef = useRef<string | null>(null);
  useEffect(() => {
    if (editingRemito) return;
    if (!selectedClientId || customDiscounts.length === 0) return;
    if (lastAutoDiscClientRef.current === selectedClientId) return;
    lastAutoDiscClientRef.current = selectedClientId;
    const elegibles = customDiscounts.filter((d: any) => {
      const clientes = Array.isArray(d.eligible_clients) ? d.eligible_clients : [];
      return clientes.includes(selectedClientId) && isDiscountSelectable(d);
    });
    if (elegibles.length === 0) return;
    const best = elegibles.slice().sort((a: any, b: any) =>
      (Number(b.priority) || 0) - (Number(a.priority) || 0) ||
      (Number(b.discount_value) || 0) - (Number(a.discount_value) || 0)
    )[0];
    setSelectedDiscount(String(best.id));
    setAppliedDiscount(best);
    toast.success(`Descuento del cliente aplicado: ${best.name}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, customDiscounts, editingRemito]);

  const fetchRemitos = async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent);
    // Evitar múltiples llamadas simultáneas
    if (isFetching) {
      console.log('Ya se está cargando remitos, evitando llamada duplicada');
      return;
    }
    
    try {
      setIsFetching(true);
      if (!silent) setLoading(true);
      const response = await axios.get('/api/remitos');
      const remitosData = response.data.remitos || [];
      
      // Verificar que no haya duplicados por ID
      const uniqueRemitos = remitosData.filter((remito: any, index: number, self: any[]) => 
        index === self.findIndex((r: any) => r.id === remito.id)
      );
      
      // Preservar el estado local de whatsappSent y emailSent
      setRemitos(prevRemitos => {
        return uniqueRemitos.map((newRemito: Remito) => {
          const existingRemito = prevRemitos.find((r: Remito) => r.id === newRemito.id);
          if (existingRemito) {
            return {
              ...newRemito,
              whatsappSent: existingRemito.whatsappSent || newRemito.whatsappSent,
              emailSent: existingRemito.emailSent || newRemito.emailSent
            };
          }
          return newRemito;
        });
      });
    } catch (error: any) {
      console.error('Error fetching remitos:', error);
      if (error.response?.status === 401) {
        console.log('Usuario no autenticado');
      } else {
        if (!silent) toast.error('Error al cargar remitos');
      }
      setRemitos([]);
    } finally {
      if (!silent) setLoading(false);
      setIsFetching(false);
    }
  };
  fetchRemitosRef.current = fetchRemitos;

  const fetchClients = async () => {
    try {
      const response = await axios.get('/api/clients?limit=10000');
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
  };

  const fetchVehicles = async (clientId?: string) => {
    try {
      if (!clientId) {
        setVehicles([]);
        return;
      }
      
      const url = `/api/vehicles?clientId=${clientId}`;
      const response = await axios.get(url);
      setVehicles(response.data || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]);
    }
  };

  // Función para cargar vehículos cuando se selecciona un cliente
  const handleClientChange = async (clientId: string) => {
    if (clientId) {
      await fetchVehicles(clientId);
      // También cargar presupuestos y citas del cliente
      await fetchBudgets(clientId);
      await fetchAppointments(clientId);
    } else {
      setVehicles([]);
      setBudgets([]);
      setAppointments([]);
    }
  };

  const fetchBudgets = async (clientId?: string) => {
    try {
      let url = '/api/budgets';
      if (clientId) {
        url = `/api/budgets/by-client/${clientId}?status=approved`;
      }
      const response = await axios.get(url);
      setBudgets(response.data.budgets || response.data || []);
    } catch (error) {
      console.error('Error fetching budgets:', error);
      setBudgets([]);
    }
  };

  const fetchAppointments = async (clientId?: string) => {
    try {
      let url = '/api/appointments';
      if (clientId) {
        url = `/api/appointments/by-client/${clientId}?status=completed`;
      }
      const response = await axios.get(url);
      setAppointments(response.data.appointments || response.data || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/products?limit=all');
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  };

  const fetchPaymentOptions = async () => {
    try {
      const response = await axios.get('/api/remitos/payment-options');
      const serverOptions: Array<{ value: string; label: string; surcharge?: number }> = response.data.paymentMethods || [];
      const hasCheque = serverOptions.some((opt) => opt.value === 'cheque');
      const normalizedOptions = hasCheque
        ? serverOptions
        : [
            ...serverOptions,
            { value: 'cheque', label: 'Cheque' }
          ];
      // "Pago mixto" no es un método de la tabla: se agrega siempre al final
      setPaymentOptions([...normalizedOptions.filter((o) => o.value !== METODO_MIXTO), OPCION_MIXTO]);
      setCcIntervalOptions(response.data.ccIntervals || []);
    } catch (error) {
      console.error('Error fetching payment options:', error);
      // Opciones por defecto si falla la llamada
      const defaultOptions = [
        { value: 'efectivo', label: 'Efectivo' },
        { value: 'debito', label: 'Débito' },
        { value: 'transferencia', label: 'Transferencia' },
        { value: 'cheque', label: 'Cheque' },
        { value: 'tarjeta_3', label: 'Tarjeta 3 cuotas (+15%)' },
        { value: 'tarjeta_6', label: 'Tarjeta 6 cuotas (+24%)' },
        { value: 'cuenta_corriente', label: 'Cuenta Corriente (30 días)' }
      ];
      setPaymentOptions([...defaultOptions, OPCION_MIXTO]);
      setCcIntervalOptions([
        { value: 30, label: '30 días' }
      ]);
    }
  };

  const calculatePriceWithPaymentMethod = async (basePrice: number, paymentMethod: string, paymentInterval?: number) => {
    try {
      const response = await axios.post('/api/remitos/calculate-price', {
        basePrice,
        paymentMethod,
        paymentInterval
      });
      
      const { finalPrice, surcharge } = response.data;
      setCalculatedPrice(finalPrice);
      setPriceSurcharge(surcharge);
      return finalPrice;
    } catch (error) {
      console.error('Error calculating price:', error);
      // En caso de error, usar precio base sin recargo
      setCalculatedPrice(basePrice);
      setPriceSurcharge(0);
      return basePrice;
    }
  };

  // Renglones que entran en el descuento (mismo criterio que el server,
  // server/utils/discountCalc.js). Los ítems libres (sin artículo del
  // catálogo) solo si alguien tildó su casilla; los tildados entran siempre.
  // Pasarle los items ya enganchados al catálogo (itemsVinculados).
  const itemsEnDescuento = (discount: any, items: RemitoItem[], clienteId: string | null | undefined = selectedClientId): RemitoItem[] => {
    if (!discount) return [];
    const eligibleProducts = Array.isArray(discount.eligible_products) ? discount.eligible_products : [];
    const eligibleServices = serviciosElegibles(discount);
    const eligibleClients = Array.isArray(discount.eligible_clients) ? discount.eligible_clients : [];

    if (eligibleClients.length > 0) {
      if (!clienteId || !eligibleClients.includes(clienteId)) {
        return [];
      }
    }
    const { automaticos, tildados } = separarPorDecision(items);

    // Clasificación robusta a español/inglés: los items guardan el tipo como
    // 'producto'/'servicio' (o a veces 'product'/'service'). Comparar solo
    // contra 'product' hacía que un producto ('producto') pasara como servicio.
    const isProductItem = (item: any) => {
      const t = String(item.type || '').toLowerCase();
      if (t === 'product' || t === 'producto') return true;
      if (t === 'service' || t === 'servicio') return false;
      return String(item.name || '').toLowerCase().includes('producto');
    };

    // Servicios que el descuento deja afuera ("todos menos estos")
    const excludedServices = serviciosExcluidos(discount);

    // Determinar a qué tipo de items aplicar el descuento
    let targetItems = automaticos.filter(item => !esServicioExcluido(item, excludedServices));
    if (discount.applies_to === 'services') {
      // Solo servicios; con lista específica, solo los de la lista (por nombre)
      targetItems = automaticos.filter(item => entraEnDescuentoDeServicios(item, eligibleServices, (it) => !isProductItem(it), excludedServices));
    } else if (discount.applies_to === 'products') {
      // Solo productos
      targetItems = automaticos.filter(item => {
        const isProduct = isProductItem(item);
        const matchesProduct = eligibleProducts.length === 0
          || eligibleProducts.includes((item as any).productId)
          || eligibleProducts.includes(item.name);
        return isProduct && matchesProduct;
      });
    }
    // Si applies_to es 'all' o no está definido, usar todos los items
    if (discount.applies_to === 'all' && (eligibleProducts.length > 0 || eligibleServices.length > 0)) {
      targetItems = automaticos.filter(item => {
        const isProduct = isProductItem(item);
        const matchesProduct = eligibleProducts.length > 0
          && (eligibleProducts.includes((item as any).productId) || eligibleProducts.includes(item.name));
        const matchesService = eligibleServices.length > 0 && entraEnDescuentoDeServicios(item, eligibleServices, () => false, excludedServices);
        return (isProduct && matchesProduct) || matchesService;
      });
    }
    return [...targetItems, ...tildados];
  };

  // Función para calcular el monto del descuento
  const calculateDiscountAmount = (discount: any, items: RemitoItem[]) => {
    if (!discount) return 0;
    const targetItems = itemsEnDescuento(discount, items);
    const totalTargetItems = targetItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    
    if (discount.min_amount && totalTargetItems < discount.min_amount) {
      return 0;
    }
    
    let discountAmount = 0;
    if (discount.discount_type === 'percentage') {
      discountAmount = (totalTargetItems * discount.discount_value) / 100;
    } else {
      discountAmount = Math.min(discount.discount_value, totalTargetItems);
    }
    
    return discountAmount;
  };

  // Funciones para manejar cupones
  const handleCouponApplied = (couponData: any) => {
    setAppliedCoupon(couponData);
  };

  const handleCouponRemoved = () => {
    setAppliedCoupon(null);
  };

  // Recalcular descuento del cupón cuando cambian los items
  useEffect(() => {
    if (!appliedCoupon?.coupon) return;

    const coupon = appliedCoupon.coupon;
    const currentItems = items.map(item => ({
      type: item.type || 'service',
      totalPrice: (item.quantity || 0) * (item.unitPrice || 0)
    }));
    const currentTotal = currentItems.reduce((sum, i) => sum + (i.totalPrice || 0), 0);

    let applicableAmount = currentTotal;
    if (coupon.applies_to === 'products') {
      applicableAmount = currentItems
        .filter((i: any) => i.type === 'product' || i.type === 'producto')
        .reduce((sum: number, i: any) => sum + (i.totalPrice || 0), 0);
    } else if (coupon.applies_to === 'services') {
      applicableAmount = currentItems
        .filter((i: any) => i.type === 'service' || i.type === 'servicio')
        .reduce((sum: number, i: any) => sum + (i.totalPrice || 0), 0);
    }

    let discountAmount = 0;
    if (coupon.discount_type === 'percentage') {
      discountAmount = (applicableAmount * coupon.discount_value) / 100;
      if (coupon.max_discount) discountAmount = Math.min(discountAmount, coupon.max_discount);
    } else {
      discountAmount = Math.min(coupon.discount_value, applicableAmount);
    }

    if (Math.abs(discountAmount - (appliedCoupon.discount_amount || 0)) > 0.01) {
      setAppliedCoupon((prev: any) => ({
        ...prev,
        discount_amount: discountAmount,
        applicable_amount: applicableAmount,
        final_amount: currentTotal - discountAmount
      }));
    }
  }, [items]);

  // Base del cobro: ítems menos descuento y cupón, sin recargo (lo que reparte el pago mixto)
  const baseCobrable = () => {
    const subtotal = items.reduce((t, it) => t + ((it.quantity || 0) * (it.unitPrice || 0)), 0);
    const descuento = appliedDiscount ? calculateDiscountAmount(appliedDiscount, itemsVinculados) : 0;
    const cupon = appliedCoupon?.discount_amount ? Number(appliedCoupon.discount_amount) : 0;
    return Math.max(0, subtotal - descuento - cupon);
  };

  // Vista previa con la MISMA cuenta que hace onSubmit: descuento sobre los ítems
  // que alcanza, y recargo del medio de pago sobre la base ya descontada.
  const renderTotales = () => {
    const subtotal = items.reduce((t, it) => t + ((it.quantity || 0) * (it.unitPrice || 0)), 0);
    const descuento = appliedDiscount ? calculateDiscountAmount(appliedDiscount, itemsVinculados) : 0;
    const cupon = appliedCoupon?.discount_amount ? Number(appliedCoupon.discount_amount) : 0;
    const base = Math.max(0, subtotal - descuento - cupon);
    // calculatedPrice puede estar calculado sobre otra base; de ahí solo tomamos la tasa.
    const tasaRecargo = calculatedPrice > 0 && priceSurcharge > 0 ? priceSurcharge / Math.max(1, calculatedPrice - priceSurcharge) : 0;
    // Cobro mixto: el recargo sale de cada renglón (solo sobre la parte con tarjeta)
    const recargo = watch('paymentMethod') === METODO_MIXTO
      ? repartirLineas(base, pagosMixtos, recargosPorMetodo).recargoTotal
      : Math.round(base * tasaRecargo * 100) / 100;
    const sinServicios = appliedDiscount ? serviciosExcluidos(appliedDiscount) : [];
    const alcance = (appliedDiscount?.applies_to === 'services' ? 'solo servicios'
      : appliedDiscount?.applies_to === 'products' ? 'solo productos' : '')
      + (sinServicios.length ? `${appliedDiscount?.applies_to === 'services' ? ', ' : ''}sin ${sinServicios.join(', ')}` : '');
    const hayAjustes = Boolean(appliedDiscount) || cupon > 0 || recargo > 0;
    return (
      <div className="mt-4 p-3 bg-muted rounded-lg space-y-1">
        {hayAjustes && (
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Subtotal</span>
            <span>{formatCurrencyAR(subtotal)}</span>
          </div>
        )}
        {appliedDiscount && (
          <div className="flex justify-between text-sm">
            <span>{appliedDiscount.name}{alcance ? ` (${alcance})` : ''}</span>
            {descuento > 0
              ? <span className="text-green-600 dark:text-green-400">−{formatCurrencyAR(descuento)}</span>
              : <span className="text-muted-foreground">no alcanza a estos ítems</span>}
          </div>
        )}
        {cupon > 0 && (
          <div className="flex justify-between text-sm">
            <span>Cupón{appliedCoupon?.coupon?.code ? ` ${appliedCoupon.coupon.code}` : ''}</span>
            <span className="text-green-600 dark:text-green-400">−{formatCurrencyAR(cupon)}</span>
          </div>
        )}
        {recargo > 0 && (
          <div className="flex justify-between text-sm">
            <span>Recargo medio de pago</span>
            <span>+{formatCurrencyAR(recargo)}</span>
          </div>
        )}
        <div className={`flex justify-between items-center ${hayAjustes ? 'pt-1 border-t' : ''}`}>
          <span className="text-sm font-medium">{hayAjustes ? 'Total' : 'Total Estimado'}:</span>
          <span className="text-lg font-bold">{formatCurrencyAR(base + recargo)}</span>
        </div>
      </div>
    );
  };

  const onSubmit = async (data: any) => {
    if (isFetching) {
      console.log('Ya se está cargando remitos, evitando llamada duplicada');
      return;
    }
    
    try {
      const itemsSum = items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
      // Descuento total (personalizado + cupón). Si se quitó el descuento, queda 0.
      let discountTotal = 0;
      if (appliedDiscount) discountTotal += calculateDiscountAmount(appliedDiscount, itemsVinculados);
      if (appliedCoupon && appliedCoupon.discount_amount) discountTotal += Number(appliedCoupon.discount_amount || 0);
      // El descuento se aplica UNA sola vez acá. El recargo del medio de pago se
      // calcula SOBRE la base ya con descuento (igual que el PDF), así el total
      // siempre queda = (itemsSum - descuento) * factorMedioPago. No usamos el
      // estado `calculatedPrice` porque puede quedar viejo (base sin/ con descuento
      // según la última interacción) y producía un doble descuento fantasma.
      const baseConDescuento = Math.max(0, itemsSum - discountTotal);
      let finalPrice = baseConDescuento;
      // Cobro mixto: se reparte la base entre los medios; el server valida y recalcula
      let pagosPayload: any[] | undefined;
      if (data.paymentMethod === METODO_MIXTO) {
        const reparto = repartirLineas(baseConDescuento, pagosMixtos, recargosPorMetodo);
        if (!reparto.ok) {
          toast.error(reparto.error || 'Revisá el pago mixto');
          return;
        }
        finalPrice = reparto.total;
        pagosPayload = payloadPagos(pagosMixtos);
      } else if (data.paymentMethod) {
        try {
          const interval = data.paymentInterval ? parseInt(data.paymentInterval) : undefined;
          finalPrice = await calculatePriceWithPaymentMethod(baseConDescuento, data.paymentMethod, interval);
        } catch {
          finalPrice = baseConDescuento;
        }
      }
      const remitoData = {
        ...data,
        // Enganchados al catálogo: se guarda el artículo y el tipo real
        items: itemsVinculados,
        totalQuantity: items.reduce((sum, item) => sum + (item.quantity || 0), 0),
        // Subtotal SIN descuento (lo que muestra la vista como "Subtotal items").
        estimatedValue: itemsSum,
        totalContado: Math.max(0, Number(finalPrice) || baseConDescuento),
        paymentMethod: data.paymentMethod,
        paymentInterval: data.paymentInterval ? parseInt(data.paymentInterval) : null,
        // Cuenta que recibe la plata (solo transferencia/débito/QR; si no, se limpia)
        accountId: ['transferencia', 'debito', 'qr'].includes(data.paymentMethod) ? (data.accountId || null) : null,
        ...(pagosPayload ? { payments: pagosPayload } : {}),
        // Km del vehículo: entero, o se omite (undefined no viaja en el JSON)
        // para no pisar con un vacío lo que ya estaba cargado.
        mileage: String(data.mileage ?? '').trim() !== '' && !isNaN(parseInt(data.mileage, 10))
          ? parseInt(data.mileage, 10)
          : undefined,
        // Incluir información de descuentos
        appliedDiscount: appliedDiscount,
        appliedCoupon: appliedCoupon,
        discountAmount: discountTotal,
        ...(data.paymentMethod === 'cheque' && (chequeNumero || chequeBanco || chequeVencimiento)
          ? { chequeData: { checkNumber: chequeNumero, bank: chequeBanco, dueDate: chequeVencimiento } }
          : {})
      };

      if (!editingRemito && data.clientId && !(await ensureClientData(data.clientId))) {
        return;
      }
      if (editingRemito) {
        await axios.put(`/api/remitos/${editingRemito.id}`, remitoData);
        toast.success('Remito actualizado exitosamente');
      } else {
        // Crear y emitir automáticamente
        const createRes = await axios.post('/api/remitos', remitoData);
        const created = createRes.data;
        try {
          await axios.post(`/api/remitos/${created.id}/issue`);
          toast.success('Remito creado y emitido exitosamente');
        } catch (issueErr: any) {
          console.error('Error emitiendo remito recién creado:', issueErr);
          // Si falla la emisión, mantener creado como borrador
          toast.error(issueErr.response?.data?.error || 'Remito creado, pero falló la emisión');
        }
      }
      
      // Agregar un delay antes de recargar para evitar duplicados
      setTimeout(async () => {
        await fetchRemitos();
      }, 500);
      
      handleCloseModal();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al guardar remito');
    }
  };

  const handleEdit = (remito: Remito) => {
    setEditingRemito(remito);
    setItems(remito.items || []);
    setSelectedPaymentMethod(remito.paymentMethod || '');
    setPagosMixtos(esMixto(remito) ? lineasDesdePagos(remito.payments) : []);
    setSelectedPaymentInterval(remito.paymentInterval || null);
    // Restaurar descuento aplicado (si existía)
    try {
      const disc = typeof (remito as any).appliedDiscount === 'string'
        ? JSON.parse((remito as any).appliedDiscount)
        : (remito as any).appliedDiscount;
      setAppliedDiscount(disc || null);
    } catch {
      setAppliedDiscount(null);
    }
    reset({
      clientId: remito.clientId,
      vehicleId: remito.vehicleId,
      type: remito.type,
      serviceType: remito.serviceType,
      estimatedValue: remito.estimatedValue,
      observations: remito.observations,
      relatedBudgetId: remito.relatedBudgetId,
      relatedAppointmentId: remito.relatedAppointmentId,
      paymentMethod: remito.paymentMethod || '',
      paymentInterval: remito.paymentInterval ? String(remito.paymentInterval) : '',
      accountId: (remito as any).accountId || '',
      createdAt: remito.createdAt ? toYMD(new Date(remito.createdAt)) : hoyLocal(),
      mileage: (remito as any).mileage != null ? String((remito as any).mileage) : ''
    });
    // Forzar setValue por si reset no propaga al Select Radix
    setTimeout(() => {
      if (remito.paymentMethod) setValue('paymentMethod', remito.paymentMethod);
      if (remito.paymentInterval) setValue('paymentInterval', String(remito.paymentInterval));
      if ((remito as any).accountId) setValue('accountId', (remito as any).accountId);
    }, 0);
    setShowModal(true);
  };

  const handleView = (remito: Remito) => {
    setViewingRemito(remito);
    setShowViewModal(true);
    // Inicializar edicion de km / vehiculo
    const mi = (remito as any).mileage;
    setRemitoKmInput(mi != null && mi !== '' ? String(mi) : '');
    setRemitoVehicleId((remito as any).vehicleId || '');
    setEditingRemitoKm(false);
    setEditingRemitoVehicle(false);
    setViewClientVehicles([]);
    if (remito.clientId) {
      axios.get(`/api/vehicles?clientId=${remito.clientId}`)
        .then((r) => setViewClientVehicles(r.data || []))
        .catch(() => setViewClientVehicles([]));
    }
  };

  const saveRemitoKm = async () => {
    if (!viewingRemito) return;
    const km = parseInt(remitoKmInput, 10);
    if (isNaN(km) || km < 0) { toast.error('Ingresá un km válido'); return; }
    // Km menor al de la ficha: avisar (suele ser typo; la ficha no retrocede)
    const fichaKm = Number((viewClientVehicles.find((v: any) => v.id === viewingRemito.vehicleId) as any)?.mileage) || 0;
    if (fichaKm > 0 && km < fichaKm) {
      const ok = await confirmDialog({
        title: 'Km menor al registrado',
        description: `La ficha del auto tiene ${fichaKm.toLocaleString('es-AR')} km y estás cargando ${km.toLocaleString('es-AR')}. Si es un error de tipeo, corregilo. La ficha del vehículo no retrocede.`,
        confirmText: 'Guardar igual'
      });
      if (!ok) return;
    }
    try {
      setSavingRemitoKm(true);
      await axios.put(`/api/remitos/${viewingRemito.id}/mileage`, { mileage: km });
      setViewingRemito((prev: any) => (prev ? { ...prev, mileage: km } : prev));
      setEditingRemitoKm(false);
      toast.success('Km guardado');
      fetchRemitos({ silent: true });
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo guardar el km');
    } finally {
      setSavingRemitoKm(false);
    }
  };

  const saveRemitoVehicle = async () => {
    if (!viewingRemito) return;
    try {
      setSavingRemitoVehicle(true);
      await axios.put(`/api/remitos/${viewingRemito.id}/vehicle`, { vehicleId: remitoVehicleId || null });
      const v = viewClientVehicles.find((x: any) => String(x.id) === String(remitoVehicleId));
      setViewingRemito((prev: any) => (prev ? {
        ...prev,
        vehicleId: remitoVehicleId || null,
        licensePlate: v?.licensePlate || null,
        vehicleInfo: v ? `${v.make || v.brand || ''} ${v.model || ''}`.trim() : null,
      } : prev));
      setEditingRemitoVehicle(false);
      toast.success('Vehículo actualizado');
      fetchRemitos({ silent: true });
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo actualizar el vehículo');
    } finally {
      setSavingRemitoVehicle(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!await confirmDialog('¿Está seguro de eliminar este remito?')) return;
    
    try {
      await axios.delete(`/api/remitos/${id}`);
      toast.success('Remito eliminado exitosamente');
      await fetchRemitos();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar remito');
    }
  };

  // Emitir sin medio de pago deja el remito colgado: no entra ni a caja ni a la
  // cuenta corriente. Si falta, se pregunta acá en vez de fallar al confirmar.
  const [remitoSinMedio, setRemitoSinMedio] = useState<any | null>(null);
  const [medioElegido, setMedioElegido] = useState('');

  const handleIssue = async (id: string, metodo?: string, pagos?: any[]) => {
    if (isFetching || processingActions.has(`issue-${id}`)) {
      console.log('Ya se está procesando esta acción, evitando llamada duplicada');
      return;
    }

    const remitoToIssue = (remitos || []).find((r: any) => r.id === id);
    if (remitoToIssue?.clientId && !(await ensureClientData(remitoToIssue.clientId))) {
      return;
    }

    if (!metodo && !String(remitoToIssue?.paymentMethod || '').trim()) {
      setMedioElegido('');
      setRemitoSinMedio(remitoToIssue || { id });
      return;
    }

    setProcessingActions(prev => new Set(prev).add(`issue-${id}`));

    try {
      await axios.post(`/api/remitos/${id}/issue`, pagos && pagos.length ? { payments: pagos } : (metodo ? { paymentMethod: metodo } : {}));
      toast.success('Remito emitido exitosamente');
      
      // Agregar un delay antes de recargar para evitar duplicados
      setTimeout(async () => {
      await fetchRemitos();
      }, 500);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al emitir remito');
    } finally {
      setProcessingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(`issue-${id}`);
        return newSet;
      });
    }
  };



  const requestReopen = (remito: Remito) => {
    setReopenTarget(remito);
  };

  const confirmReopen = async () => {
    if (!reopenTarget) return;
    const id = reopenTarget.id;
    setReopenTarget(null);
    if (processingActions.has(`reopen-${id}`)) return;
    setProcessingActions(prev => new Set(prev).add(`reopen-${id}`));
    try {
      await axios.post(`/api/remitos/${id}/reopen`);
      toast.success('Remito reabierto');
      setTimeout(async () => { await fetchRemitos(); }, 300);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al reabrir remito');
    } finally {
      setProcessingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(`reopen-${id}`);
        return newSet;
      });
    }
  };

  const handleCancel = async (id: string) => {
    if (isFetching || processingActions.has(`cancel-${id}`)) {
      console.log('Ya se está procesando esta acción, evitando llamada duplicada');
      return;
    }

    setProcessingActions(prev => new Set(prev).add(`cancel-${id}`));

    try {
      await axios.post(`/api/remitos/${id}/cancel`, { reason: 'Cancelado por el usuario' });
      toast.success('Remito cancelado');

      // Agregar un delay antes de recargar para evitar duplicados
      setTimeout(async () => {
      await fetchRemitos();
      }, 500);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al cancelar remito');
    } finally {
      setProcessingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(`cancel-${id}`);
        return newSet;
      });
    }
  };

  const handlePrint = (remito: Remito) => {
    setPrintingRemito(remito);
    setShowPrintModal(true);
  };

  const handlePaymentMethod = (remito: Remito) => {
    setSelectedRemitoForPayment(remito);
    setPagosMixtosModal(esMixto(remito) ? lineasDesdePagos(remito.payments) : []);
    setShowPaymentModal(true);
  };

  // Manejar facturación de remito
  const handleInvoice = (remito: Remito) => {
    if (remito.invoicedAt) {
      toast.error('Este remito ya fue facturado');
      return;
    }
    if (remito.status !== 'issued') {
      toast.error('Solo se pueden facturar remitos emitidos');
      return;
    }
    setSelectedRemitoForInvoice(remito);
    setShowInvoiceModal(true);
  };

  const handleViewInvoicePdf = async (remito: Remito) => {
    try {
      // Buscar la factura asociada al remito (server-side: no depende de la
      // paginación de la lista y contempla facturas multi-remito).
      const token = localStorage.getItem('token');
      let invoice: any;
      try {
        const { data } = await axios.get(`/api/invoices/by-remito/${remito.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        invoice = data.invoice;
      } catch (e: any) {
        if (e?.response?.status === 404) {
          toast.error('No se encontró la factura asociada');
          return;
        }
        throw e;
      }
      if (!invoice) {
        toast.error('No se encontró la factura asociada');
        return;
      }
      const res = await axios.post(`/api/invoices/${invoice.id}/generate-pdf`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const pv = String(invoice.afipPtoVta || 2).padStart(4, '0');
      const nro = String(invoice.afipCbteNro || 0).padStart(8, '0');
      const filename = invoice.afipCbteNro
        ? `Factura-${invoice.type}-${pv}-${nro}.pdf`
        : `Factura-${invoice.invoiceNumber}.pdf`;
      openPdf({ blob, filename });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Error al abrir PDF de factura');
    }
  };

  const confirmInvoice = async () => {
    if (!selectedRemitoForInvoice) return;

    setIsCreatingInvoice(true);
    try {
      const response = await axios.post(`/api/invoices/from-remito/${selectedRemitoForInvoice.id}`);
      toast.success(`Factura ${response.data.invoiceNumber} creada exitosamente`);
      setShowInvoiceModal(false);
      setSelectedRemitoForInvoice(null);
      await fetchRemitos();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al crear factura');
    } finally {
      setIsCreatingInvoice(false);
    }
  };


// Un nombre de ítem trae una LISTA agrupada solo si la coma va seguida de
// espacio y letra ("Filtro aceite, Filtro aire"). La coma decimal ("3,78L")
// o pegada a un número nunca es lista: partirla rompía el nombre en dos.
const COMA_DE_LISTA = /,s+(?=[A-Za-zÁÉÍÓÚáéíóúÑñ])/;
const esNombreAgrupado = (name: any) => typeof name === 'string' && COMA_DE_LISTA.test(name);
const partirNombres = (name: string) => name.split(COMA_DE_LISTA).map((n) => n.trim()).filter(Boolean);

  // Utilidad: calcular items procesados tal como se muestran en la vista
  const computeProcessedItems = (remito: Remito) => {
    const baseItems = Array.isArray(remito.items) ? remito.items : [];

    // 1) Separar nombres agrupados por coma
    const expanded: any[] = [];
    baseItems.forEach((it: any) => {
      if (it && esNombreAgrupado(it.name)) {
        const names = partirNombres(it.name);
        const qty = Number(it.quantity || 1);
        const total = Number(it.totalPrice || 0);
        const unit = Number(it.unitPrice || 0);

        const perItemTotal = total > 0 && names.length > 0 ? total / names.length : unit;
        const perItemUnit = unit > 0 ? unit : perItemTotal;

        names.forEach((n: string) => {
          expanded.push({
            ...it,
            name: n,
            quantity: 1,
            unitPrice: perItemUnit,
            totalPrice: perItemTotal,
          });
        });
      } else {
        expanded.push({
          ...it,
          quantity: Number(it.quantity || 1),
          unitPrice: Number(it.unitPrice || 0),
          totalPrice: Number(it.totalPrice || (Number(it.unitPrice || 0) * Number(it.quantity || 1))),
        });
      }
    });

    // 2) Agrupar por nombre y consolidar
    const itemGroups = new Map<string, any[]>();
    expanded.forEach((it: any) => {
      if (!it || !it.name) return;
      const key = it.name.trim().toLowerCase();
      if (!itemGroups.has(key)) itemGroups.set(key, []);
      itemGroups.get(key)!.push(it);
    });

    const finalItems: any[] = [];
    itemGroups.forEach((items) => {
      if (items.length === 1) {
        finalItems.push(items[0]);
      } else {
        const totalQuantity = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        const totalAmount = items.reduce((sum, item) => sum + Number(item.totalPrice || 0), 0);
        const avgUnitPrice = totalQuantity > 0 ? totalAmount / totalQuantity : 0;
        finalItems.push({
          ...items[0],
          quantity: totalQuantity,
          unitPrice: avgUnitPrice,
          totalPrice: totalAmount,
        });
      }
    });

    return finalItems;
  };

  const handleSendWhatsApp = async (remito: Remito) => {
    if (isFetching || processingActions.has(`whatsapp-${remito.id}`)) {
      console.log('Ya se está procesando esta acción, evitando llamada duplicada');
      return;
    }
    
    setProcessingActions(prev => new Set(prev).add(`whatsapp-${remito.id}`));
    
    try {
      // Enviar por WhatsApp usando el endpoint del servidor
      const response = await axios.post(`/api/remitos/${remito.id}/send-whatsapp`);
      
      if (response.data.pdfSent) {
        toast.success(`Remito y PDF enviados por WhatsApp a ${response.data.sentTo}`);
      } else {
        toast.success(`Remito enviado por WhatsApp a ${response.data.sentTo}`);
      }
      
      // Actualizar el estado local inmediatamente para mostrar el icono verde
      setRemitos(prevRemitos => 
        prevRemitos.map(r => 
          r.id === remito.id 
            ? { ...r, whatsappSent: true }
            : r
        )
      );
      
    } catch (error: any) {
      console.error('Error enviando por WhatsApp:', error);
      let errorMessage = 'Error al enviar por WhatsApp';
      
      if (error.response?.status === 503) {
        errorMessage = 'WhatsApp no está conectado. Verifica la conexión.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.details) {
        errorMessage = `Error: ${error.response.data.details}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setProcessingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(`whatsapp-${remito.id}`);
        return newSet;
      });
    }
  };

  const handleSendEmail = async (remito: Remito) => {
    try {
      // Buscar el cliente para obtener su email
      const client = clients.find(c => c.id === remito.clientId);
      if (!client || !client.email) {
        toast.error('No se encontró el email del cliente');
        return;
      }

      // Mostrar loading
      const loadingToast = toast.loading('Enviando remito por email...');
      
      // Enviar email con PDF adjunto usando el endpoint del servidor
      const response = await axios.post(`/api/remitos/${remito.id}/send-email`);
      
      // Cerrar loading y mostrar éxito
      toast.dismiss(loadingToast);
      
      if (response.data.success) {
        toast.success(`Remito enviado por email a ${response.data.sentTo}`);
        
        // Actualizar el estado local inmediatamente para mostrar el icono verde
        setRemitos(prevRemitos => 
          prevRemitos.map(r => 
            r.id === remito.id 
              ? { ...r, emailSent: true }
              : r
          )
        );
      } else {
        toast.error('Error al enviar el remito por email');
      }
      
    } catch (error: any) {
      console.error('Error enviando remito por email:', error);
      let errorMessage = 'Error al enviar por email';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleUpdatePaymentMethod = async (paymentMethod: string, paymentInterval?: number) => {
    if (!selectedRemitoForPayment) return;
    
    if (isFetching || processingActions.has(`payment-${selectedRemitoForPayment.id}`)) {
      console.log('Ya se está procesando esta acción, evitando llamada duplicada');
      return;
    }
    
    setProcessingActions(prev => new Set(prev).add(`payment-${selectedRemitoForPayment.id}`));
    
    try {
      // Calcular base real desde los ítems del remito (descontando descuento aplicado)
      const itemsSum = Array.isArray(selectedRemitoForPayment.items)
        ? selectedRemitoForPayment.items.reduce((sum: number, it: any) => sum + Number(it.totalPrice || 0), 0)
        : Number(selectedRemitoForPayment.estimatedValue || 0);
      const discountAmt = Number(selectedRemitoForPayment.discountAmount || 0);
      const baseFromItems = Math.max(0, itemsSum - discountAmt);

      // Calcular el nuevo precio basado en el método de pago (sobre la base ya con descuento)
      let pagosPayload: any[] | undefined;
      let newPrice = baseFromItems;
      if (paymentMethod === METODO_MIXTO) {
        // Cobro mixto: la base se reparte entre los medios (recargo solo sobre la parte con tarjeta)
        const reparto = repartirLineas(baseFromItems, pagosMixtosModal, recargosPorMetodo);
        if (!reparto.ok) {
          toast.error(reparto.error || 'Revisá el pago mixto');
          return;
        }
        newPrice = reparto.total;
        pagosPayload = payloadPagos(pagosMixtosModal);
      } else {
        newPrice = await calculatePriceWithPaymentMethod(baseFromItems, paymentMethod, paymentInterval);
      }
      
      // Preparar datos para enviar: estimatedValue = subtotal SIN descuento (lo que
      // muestra la vista); totalContado = final con recargo sobre la base con descuento.
      const updateData: any = {
        paymentMethod,
        estimatedValue: itemsSum,
        totalContado: newPrice,
        ...(pagosPayload ? { payments: pagosPayload } : {})
      };
      
      // Solo incluir paymentInterval si es cuenta corriente y tiene valor
      if (paymentMethod === 'cuenta_corriente' && paymentInterval) {
        updateData.paymentInterval = parseInt(paymentInterval.toString());
      }
      
      // Usar el endpoint de actualización general
      await axios.put(`/api/remitos/${selectedRemitoForPayment.id}`, updateData);
      
      toast.success('Método de pago actualizado exitosamente');
      
      // Agregar un delay antes de recargar para evitar duplicados
      setTimeout(async () => {
        await fetchRemitos();
      }, 500);
      
      setShowPaymentModal(false);
      setSelectedRemitoForPayment(null);
      setSelectedPaymentMethod('');
      setSelectedPaymentInterval(null);
      setModalCalculatedPrice(0);
      setModalPriceSurcharge(0);
      setPagosMixtosModal([]);
    } catch (error: any) {
      console.error('Error updating payment method:', error);
      toast.error(error.response?.data?.error || 'Error al actualizar método de pago');
    } finally {
      setProcessingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(`payment-${selectedRemitoForPayment.id}`);
        return newSet;
      });
    }
  };

  // Funciones para selección múltiple
  const handleSelectRemito = (remitoId: string) => {
    const newSelected = new Set(selectedRemitos);
    if (newSelected.has(remitoId)) {
      newSelected.delete(remitoId);
    } else {
      newSelected.add(remitoId);
    }
    setSelectedRemitos(newSelected);
    
    // Verificar si todos los remitos seleccionables están seleccionados
    const selectableRemitos = userRole === 'admin' 
      ? filteredRemitos 
      : filteredRemitos.filter(remito => remito.status === 'draft');
    setSelectAll(newSelected.size === selectableRemitos.length && selectableRemitos.length > 0);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRemitos(new Set());
      setSelectAll(false);
    } else {
      // Seleccionar remitos según el rol del usuario
      const selectableRemitos = userRole === 'admin' 
        ? filteredRemitos 
        : filteredRemitos.filter(remito => remito.status === 'draft');
      const allSelectableIds = new Set(selectableRemitos.map(remito => remito.id));
      setSelectedRemitos(allSelectableIds);
      setSelectAll(true);
    }
  };

  const handleBulkAction = async (action: 'delete' | 'issue' | 'print') => {
    if (selectedRemitos.size === 0) {
      toast.error('Selecciona al menos un remito');
      return;
    }

    const selectedArray = Array.from(selectedRemitos);
    setBulkActionLoading(action);

    try {
      switch (action) {
        case 'delete':
          // Para administradores, permitir eliminar cualquier remito
          if (userRole !== 'admin') {
            // Verificar que todos los remitos seleccionados estén en borrador para usuarios normales
            const nonDraftRemitos = filteredRemitos.filter(remito => 
              selectedRemitos.has(remito.id) && remito.status !== 'draft'
            );
            
            if (nonDraftRemitos.length > 0) {
              toast.error('Solo se pueden eliminar remitos en borrador');
              setBulkActionLoading(null);
              return;
            }
          }
          
          if (!await confirmDialog(`¿Estás seguro de que quieres eliminar ${selectedArray.length} remito(s)?`)) {
            setBulkActionLoading(null);
            return;
          }
          
          await Promise.all(selectedArray.map(id => axios.delete(`/api/remitos/${id}`)));
          toast.success(`${selectedArray.length} remito(s) eliminado(s) exitosamente`);
          break;

        case 'issue':
          // Verificar que los remitos seleccionados puedan ser emitidos
          const nonDraftForIssue = filteredRemitos.filter(remito => 
            selectedRemitos.has(remito.id) && remito.status !== 'draft'
          );
          
          if (nonDraftForIssue.length > 0) {
            toast.error('Solo se pueden emitir remitos en borrador');
            setBulkActionLoading(null);
            return;
          }
          
          {
            const conPendientes = selectedArray.filter((id) => {
              const r = (remitos || []).find((x: any) => x.id === id);
              const cli = (clients || []).find((c: any) => c.id === r?.clientId);
              return cli && Number((cli as any).isPlaceholder) === 1;
            });
            if (conPendientes.length > 0) {
              toast.error(`${conPendientes.length} remito(s) son de clientes "por completar" — emitilos individualmente para cargar nombre y teléfono`);
              setBulkActionLoading(null);
              return;
            }
          }
          await Promise.all(selectedArray.map(id => axios.put(`/api/remitos/${id}/issue`)));
          toast.success(`${selectedArray.length} remito(s) emitido(s) exitosamente`);
          break;

        case 'print':
          // Abrir PDFs directamente en nuevas pestañas usando endpoint GET
          try {
            const token = localStorage.getItem('token');
            const apiUrl = axios.defaults.baseURL || window.location.origin;
            
            // Construir todas las URLs primero
            const pdfUrls: Array<{ url: string; remitoNumber: string }> = [];
            for (const remitoId of selectedArray) {
              const remitoInfo = filteredRemitos.find(r => r.id === remitoId);
              const remitoNumber = remitoInfo?.remitNumber || remitoId;
              const pdfUrl = `${apiUrl}/api/remitos/${remitoId}/pdf?token=${encodeURIComponent(token || '')}`;
              pdfUrls.push({ url: pdfUrl, remitoNumber });
            }
            
            // Abrir todas las ventanas de manera secuencial con delay
            // Usar nombres de ventana únicos para evitar que se reemplacen
            const openedWindows: Window[] = [];
            const baseTimestamp = Date.now();
            
            for (let i = 0; i < pdfUrls.length; i++) {
              const { url, remitoNumber } = pdfUrls[i];
              
              // Usar un nombre de ventana único basado en timestamp base + índice + número de remito
              const windowName = `remito_${baseTimestamp + i}_${remitoNumber}_${Math.random().toString(36).substr(2, 9)}`;
              const newWindow = window.open(url, windowName);
              
              if (newWindow) {
                openedWindows.push(newWindow);
              } else {
                console.warn(`No se pudo abrir ventana para remito ${remitoNumber}. Puede estar bloqueado por el navegador.`);
              }
              
              // Delay entre aperturas para evitar bloqueos del navegador
              // Usar un delay de 1200ms para dar tiempo al navegador
              if (i < pdfUrls.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 1200));
              }
            }
            
            // Mensaje de éxito
            if (openedWindows.length === pdfUrls.length) {
              toast.success(`${openedWindows.length} PDF(s) abriendo en nuevas pestañas`);
            } else if (openedWindows.length > 0) {
              toast.warning(`${openedWindows.length} de ${pdfUrls.length} PDF(s) abierto(s). Algunos pueden haber sido bloqueados por el navegador.`);
            } else {
              toast.error('No se pudieron abrir los PDFs. Verifica que el navegador permita popups.');
            }
          } catch (printError: any) {
            console.error('Error imprimiendo remitos:', printError);
            toast.error('Error al abrir los PDFs. Verifica la consola para más detalles.');
          }
          break;
      }

      setSelectedRemitos(new Set());
      setSelectAll(false);
      await fetchRemitos();
    } catch (error: any) {
      console.error('Error en acción en lote:', error);
      toast.error(error.response?.data?.error || `Error al realizar la acción en lote: ${error.message}`);
    } finally {
      setBulkActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClass('remito', status)}`}>
      {statusLabel('remito', status) || 'Desconocido'}
    </span>
  );

  const getRemitoAgeDays = (createdAt: string) => {
    if (!createdAt) return 0;
    const created = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  };

  const getRemitoAgeLabel = (days: number) => {
    if (days <= 0) return 'hoy';
    if (days === 1) return '1 dia';
    return `${days} dias`;
  };

  const getRemitoAgeBadgeClass = (days: number) => {
    if (days <= 2) return 'bg-green-100 text-green-800';
    if (days <= 6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getTypeBadge = (type: string) => {
    const typeConfig = {
      service: { label: 'Servicio', color: 'bg-purple-100 text-purple-800' },
      delivery: { label: 'Entrega', color: 'bg-orange-100 text-orange-800' },
      warranty: { label: 'Garantía', color: 'bg-green-100 text-green-800' },
      internal: { label: 'Interno', color: 'bg-gray-100 text-gray-800' }
    };
    const config = typeConfig[type as keyof typeof typeConfig];
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Nombre del método de pago: primero la lista dinámica (Configuración →
  // Métodos de pago), así cualquier método nuevo se muestra bien.
  const payMethodName = (code?: string) => {
    if (!code) return '';
    const found = paymentOptions.find((o) => o.value === code);
    if (found) return found.label;
    return getPaymentMethodLabel(code); // fallback: mapa fijo, y si no, el código
  };

  const getPaymentMethodDescription = (paymentMethod?: string, _paymentInterval?: number) => {
    if (!paymentMethod) return 'No especificado';
    return payMethodName(paymentMethod) || 'No especificado';
  };

  // Texto del cobro de un remito: el medio, o los renglones si es un pago mixto
  const describirPago = (remito: any) =>
    esMixto(remito) && Array.isArray(remito.payments) && remito.payments.length
      ? `${LABEL_MIXTO}: ${resumenPagos(remito.payments, payMethodName)}`
      : getPaymentMethodDescription(remito?.paymentMethod, remito?.paymentInterval);

  const getClientName = (client: Client) => {
    return client.type === 'company'
      ? client.companyName || 'Empresa sin nombre'
      : `${client.firstName || ''} ${client.lastName || ''}`.trim();
  };

  const getVehicleInfo = (vehicle: Vehicle) => {
    return `${vehicle.make || vehicle.brand || ''} ${vehicle.model}${vehicle.year ? ` (${vehicle.year})` : ''} - ${vehicle.licensePlate}`;
  };

  const handleVehicleCreated = async (createdVehicle: any) => {
    await fetchVehicles(selectedClientId);
    // El combobox arma sus opciones desde selectedClient.vehicles (estado clients),
    // no desde el estado vehicles. Hay que agregar el vehiculo nuevo ahi para que
    // aparezca en la lista y quede seleccionable.
    setClients((prev) => prev.map((c) => c.id === selectedClientId
      ? { ...c, vehicles: [...(((c as any).vehicles) || []), createdVehicle] }
      : c));
    if (createdVehicle?.id) {
      setValue('vehicleId', createdVehicle.id);
    }
  };

  const selectedClient = clients.find(c => c.id === selectedClientId);
  const clientVehiclesRaw = selectedClient && (selectedClient as any).vehicles ? (selectedClient as any).vehicles : [];
  // Consumidor Final es un cliente compartido: NO mostrar el pool de todos los
  // autos que pasaron por CF (privacidad + confusión). Solo el vehículo de ESTE
  // remito; para poner otro se usa "Crear vehículo".
  const isConsumidorFinal = selectedClientId === 'consumidor-final';
  const clientVehicles = isConsumidorFinal
    ? clientVehiclesRaw.filter((v: any) => v.id === watch('vehicleId'))
    : clientVehiclesRaw;
  const selectedVehicle = clientVehicles.find((v: any) => v.id === watch('vehicleId')) || null;
  // Km ya registrado en la ficha, para sugerirlo y avisar si cargan uno menor
  const fichaKmSeleccionado = Number((selectedVehicle as any)?.mileage) || 0;
  const kmMenorQueFicha = (() => {
    const ingresado = parseInt(String(watch('mileage') ?? ''), 10);
    return fichaKmSeleccionado > 0 && !isNaN(ingresado) && ingresado < fichaKmSeleccionado;
  })();
  const vehicleComboboxOptions = clientVehicles.map((vehicle: any) => ({
    id: vehicle.id,
    plate: vehicle.licensePlate,
    brand: vehicle.make || vehicle.brand,
    model: vehicle.model,
    year: vehicle.year
  }));

  const filteredRemitos = (remitos || []).filter(remito => {
    // Filtro por factura (llega desde el botón "Ver remito"): solo esos ids
    if (remitoIdsFilter.length > 0 && !remitoIdsFilter.includes(remito.id)) return false;

    const matchesSearch = !searchTerm ||
      remito.remitNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      remito.clientName.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = true;
    if (remitoFilter === 'draft') {
      matchesStatus = remito.status === 'draft';
    } else if (remitoFilter === 'issued_not_invoiced') {
      matchesStatus = remito.status === 'issued' && !remito.invoicedAt;
    } else if (remitoFilter === 'issued_not_paid') {
      matchesStatus = remito.status === 'issued' && !remito.paidAt;
    }

    const matchesPayment =
      paymentFilter === 'all'
        ? true
        : (remito.paymentMethod === paymentFilter
          // Un cobro mixto aparece al filtrar por cualquiera de sus medios
          || (esMixto(remito) && (remito.payments || []).some((p) => p.method === paymentFilter)));

    let matchesDate = true;
    if (dateFrom) {
      const start = new Date(`${dateFrom}T00:00:00`);
      const created = new Date(remito.createdAt);
      if (created < start) matchesDate = false;
    }
    if (dateTo) {
      const end = new Date(`${dateTo}T23:59:59`);
      const created = new Date(remito.createdAt);
      if (created > end) matchesDate = false;
    }

    return matchesSearch && matchesStatus && matchesPayment && matchesDate;
  });

  // Calcular paginación
  const totalPages = Math.ceil(filteredRemitos.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRemitos = filteredRemitos.slice(startIndex, endIndex);
  const totalResults = filteredRemitos.length;
  const startResult = filteredRemitos.length === 0 ? 0 : startIndex + 1;
  const endResult = Math.min(endIndex, filteredRemitos.length);

  // Funciones de navegación de paginación
  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);

  // Resetear página cuando cambia el filtro
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, remitoFilter, paymentFilter, dateFrom, dateTo]);

  const handleDownloadPDF = async (remitoId: string) => {
    try {
      const remitoInfo = remitos.find((remito) => remito.id === remitoId);
      const fallbackId = remitoId?.slice(-6) || 'documento';
      const filename = remitoInfo?.remitNumber
        ? `Remito-${remitoInfo.remitNumber}.pdf`
        : `Remito-${fallbackId}.pdf`;

      const response = await axios.post(`/api/remitos/${remitoId}/generate-pdf`, {}, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const result = openPdf({ blob, filename });
      toast.success(result === 'downloaded' ? 'PDF descargado' : 'PDF abierto');
    } catch (error) {
      console.error('Error descargando PDF:', error);
      toast.error('Error al abrir el PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <div className="space-y-6">
      {dialogoRetiro}
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">{isCreatePage ? 'Nuevo Remito' : 'Remitos'}</h2>
        </div>
        {!isCreatePage && (
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <button
            onClick={() => {
              navigate('/remitos/new');
            }}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo remito
            <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
              N
            </kbd>
          </button>
        </div>
        )}
      </div>

      {isCreatePage && (
        <Card>
          <CardContent className="p-6">
            <form id="remito-form-page" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clientId" className="font-medium text-sm">Cliente *</Label>
                  <ClientCombobox
                    clients={clients || []}
                    selectedClient={selectedClient || null}
                    onSelect={(client) => {
                      if (client) {
                        setValue('clientId', client.id, { shouldValidate: true });
                        setValue('vehicleId', '');
                        handleClientChange(client.id);
                      } else {
                        setValue('clientId', '');
                        setValue('vehicleId', '');
                      }
                    }}
                    placeholder="Buscar cliente..."
                    getClientName={getClientName}
                    onCreateClient={(query) => {
                      const params = new URLSearchParams();
                      params.set('returnTo', `${location.pathname}${location.search || ''}`);
                      if (query?.trim()) params.set('name', query.trim());
                      navigate(`/clients/new?${params.toString()}`);
                    }}
                  />
                  <ClientQuickPicks
                    onConsumidorFinal={() => { setValue('clientId', 'consumidor-final', { shouldValidate: true }); setValue('vehicleId', ''); handleClientChange('consumidor-final'); }}
                    onPlateResolved={({ client, vehicle }) => {
                      // El combobox de vehículos lee client.vehicles → adjuntar el vehículo resuelto
                      const clientVehicles = vehicle?.id ? [vehicle] : [];
                      setClients((prev: any[]) => prev.some((c: any) => c.id === client.id)
                        ? prev.map((c: any) => c.id === client.id
                            ? { ...c, vehicles: (c.vehicles && c.vehicles.length) ? c.vehicles : clientVehicles }
                            : c)
                        : [...prev, { id: client.id, firstName: client.name, lastName: '', phone: client.phone || '', isPlaceholder: client.placeholder ? 1 : 0, vehicles: clientVehicles }]);
                      setValue('clientId', client.id, { shouldValidate: true });
                      handleClientChange(client.id);
                      if (vehicle?.id) setValue('vehicleId', vehicle.id, { shouldValidate: true });
                    }}
                  />
                  <input type="hidden" {...register('clientId', { required: 'Cliente es requerido' })} />
                  {errors.clientId && <p className="text-sm text-red-600 mt-1">{errors.clientId.message?.toString()}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicleId" className="font-medium text-sm">Vehículo</Label>
                  <VehicleCombobox
                    vehicles={vehicleComboboxOptions}
                    selectedVehicle={selectedVehicle ? {
                      id: selectedVehicle.id,
                      plate: selectedVehicle.licensePlate,
                      brand: selectedVehicle.make || selectedVehicle.brand,
                      model: selectedVehicle.model,
                      year: selectedVehicle.year
                    } : null}
                    onSelect={(vehicle) => setValue('vehicleId', vehicle?.id || '')}
                    onCreateVehicle={(q) => { setVehiclePrefillPlate(q || ''); setVehicleModalOpen(true); }}
                    disabled={!selectedClientId}
                    placeholder={selectedClientId ? 'Buscar vehículo...' : 'Seleccione un cliente primero'}
                  />
                  {/* Km del vehículo: actualiza la ficha del auto si es mayor */}
                  {watch('vehicleId') && (
                    <div className="pt-1">
                      <Label htmlFor="mileageCreate" className="text-xs text-muted-foreground">Km del vehículo</Label>
                      <div className="mt-1 flex gap-1.5">
                        <Input
                          id="mileageCreate"
                          type="number"
                          min={0}
                          placeholder={fichaKmSeleccionado > 0 ? `Último: ${fichaKmSeleccionado.toLocaleString('es-AR')} km` : 'Ej: 85000'}
                          className="h-9"
                          {...register('mileage')}
                        />
                        <OdometerScanButton onKm={(km) => setValue('mileage', String(km))} kmReferencia={fichaKmSeleccionado || undefined} className="h-9 shrink-0" />
                      </div>
                      {kmMenorQueFicha && (
                        <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
                          La ficha tiene {fichaKmSeleccionado.toLocaleString('es-AR')} km. Si es un error de tipeo, corregilo — la ficha del vehículo no retrocede.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Cortesía sorpresa por reseña de Google */}
              {clientCourtesy && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-violet-500/30 bg-violet-500/10 px-3 py-2 text-sm">
                  <span>
                    🎁 <b>Cortesía por reseña{clientCourtesy.courtesyCode ? ` ${clientCourtesy.courtesyCode}` : ''}: {clientCourtesy.courtesyValue}</b>
                    {clientCourtesy.courtesyExpiresAt ? ` · vence ${new Date(clientCourtesy.courtesyExpiresAt).toLocaleDateString('es-AR')}` : ''}
                    {' '}— aplicale el descuento y marcala usada.
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 ml-auto"
                    onClick={async () => {
                      try {
                        await axios.post(`/api/reviews/${clientCourtesy.id}/courtesy-used`);
                        setClientCourtesy(null);
                        toast.success('Cortesía marcada como usada');
                      } catch (e: any) {
                        toast.error(e.response?.data?.error || 'Error al marcar la cortesía');
                      }
                    }}
                  >
                    Marcar usada
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="relatedBudgetId" className="font-medium text-sm">Presupuesto Relacionado (Opcional)</Label>
                  <Select value={watch('relatedBudgetId') || undefined} onValueChange={(value) => setValue('relatedBudgetId', value || '')}>
                    <SelectTrigger id="relatedBudgetId" className="w-full rounded-lg h-auto px-4 py-3">
                      <SelectValue placeholder="Sin presupuesto relacionado" />
                    </SelectTrigger>
                    <SelectContent>
                      {(budgets || [])
                        .filter(budget => budget.status === 'approved' || budget.status === 'completed')
                        .map((budget) => (
                          <SelectItem key={budget.id} value={budget.id}>
                            {budget.budgetNumber || budget.id} - {budget.clientName} - ${(budget.totalAmount || 0).toFixed(2)}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="relatedAppointmentId" className="font-medium text-sm">Turno Relacionado (Opcional)</Label>
                  <Select value={watch('relatedAppointmentId') || undefined} onValueChange={(value) => setValue('relatedAppointmentId', value || '')}>
                    <SelectTrigger id="relatedAppointmentId" className="w-full rounded-lg h-auto px-4 py-3">
                      <SelectValue placeholder="Sin turno relacionado" />
                    </SelectTrigger>
                    <SelectContent>
                      {(appointments || [])
                        .filter(appointment => appointment.status === 'completed' || appointment.status === 'in_progress')
                        .map((appointment) => (
                          <SelectItem key={appointment.id} value={appointment.id}>
                            {new Date(appointment.scheduledDate).toLocaleDateString('es-AR')} - {appointment.clientName} - {appointment.serviceType}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {isAdmin && !editingRemito && (
                <div className="space-y-2">
                  <Label htmlFor="createdAt" className="font-medium text-sm">Fecha del remito</Label>
                  <DatePicker
                    id="createdAt"
                    value={watch('createdAt') || todayStr}
                    onChange={(v) => setValue('createdAt', v)}
                    className="w-full rounded-lg h-auto px-4 py-3"
                  />
                  <p className="text-xs text-muted-foreground">Dejala en la fecha de hoy salvo que necesites cargar el remito con una fecha anterior.</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="type" className="font-medium text-sm">Tipo de Remito *</Label>
                    <Select value={watch('type') || ''} onValueChange={(value) => setValue('type', value, { shouldValidate: true })}>
                      <SelectTrigger id="type">
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="service">Servicio</SelectItem>
                        <SelectItem value="delivery">Entrega</SelectItem>
                        <SelectItem value="warranty">Garantia</SelectItem>
                        <SelectItem value="internal">Interno</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowObservationsModal(true)} className="rounded-xl justify-center text-xs h-8 px-2 bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-white/90 text-white dark:text-black border-black dark:border-white">
                      <span className="text-xs truncate">{watch('observations') ? watch('observations') : 'Observaciones'}</span>
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowDiscountModal(true)} className="rounded-xl justify-center text-xs h-8 px-2 bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-white/90 text-white dark:text-black border-black dark:border-white">
                      <span className="text-xs truncate">
                        {appliedDiscount ? `${appliedDiscount.name} - ${appliedDiscount.discount_type === 'percentage' ? `${appliedDiscount.discount_value}%` : `$${appliedDiscount.discount_value}`}` : 'Descuento'}
                      </span>
                    </Button>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="paymentMethod" className="font-medium text-sm">Metodo de Pago</Label>
                    <Select
                      value={watch('paymentMethod') || undefined}
                      onValueChange={(value) => {
                        setSelectedPaymentMethod(value || '');
                        setSelectedPaymentInterval(null);
                        setValue('paymentInterval', '');
                        setValue('paymentMethod', value || '');
                        if (value === METODO_MIXTO) setPagosMixtos((prev) => (prev.length ? prev : lineasIniciales()));
                        if (items.length > 0 && value !== METODO_MIXTO) {
                          const basePrice = items.reduce((total, item) => total + ((item.quantity || 0) * (item.unitPrice || 0)), 0);
                          if (basePrice > 0 && value) calculatePriceWithPaymentMethod(basePrice, value);
                        }
                      }}
                    >
                      <SelectTrigger id="paymentMethod">
                        <SelectValue placeholder="Sin metodo de pago" />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {watch('paymentMethod') === 'cheque' && (
                    <div className="rounded-lg border border-dashed p-3 space-y-2">
                      <p className="text-xs text-muted-foreground">Datos del cheque — se carga solo en cartera (Finanzas → Cheques)</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs">N° de cheque</Label>
                          <Input value={chequeNumero} onChange={(e) => setChequeNumero(e.target.value)} placeholder="12345678" />
                        </div>
                        <div>
                          <Label className="text-xs">Banco</Label>
                          <BankCombobox value={chequeBanco} onChange={setChequeBanco} />
                        </div>
                        <div>
                          <Label className="text-xs">Vencimiento</Label>
                          <DatePicker value={chequeVencimiento} onChange={setChequeVencimiento} />
                        </div>
                      </div>
                    </div>
                  )}
                  {watch('paymentMethod') === METODO_MIXTO && (
                    <PagoMixtoEditor
                      base={baseCobrable()}
                      lineas={pagosMixtos}
                      onChange={setPagosMixtos}
                      opciones={paymentOptions}
                      cuentas={financialAccounts}
                      ccIntervals={ccIntervalOptions}
                    />
                  )}
                  {['transferencia', 'debito', 'qr'].includes(watch('paymentMethod') || '') && financialAccounts.length > 0 && (
                    <div className="mt-2">
                      <Label className="font-medium text-sm">Cuenta que recibe</Label>
                      <Select value={watch('accountId') || undefined} onValueChange={(v) => setValue('accountId', v || '')}>
                        <SelectTrigger><SelectValue placeholder="¿A qué cuenta entró?" /></SelectTrigger>
                        <SelectContent>
                          {financialAccounts.filter((a) => a.kind !== 'efectivo').map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="block text-base font-semibold mb-4">Productos y Servicios</Label>
                <div className="mb-4" ref={productSearchContainerRef}>
                  <ProductCombobox
                    products={(products || []).map((product) => ({
                      ...product,
                      sellingPrice: Number(product.salePrice || 0)
                    }))}
                    selectedProduct={null}
                    onSelect={(product) => addItemFromProduct(product)}
                    onCreateRequested={addManualItem}
                    placeholder="Buscar producto o servicio..."
                  />
                </div>

                {items.length > 0 && (
                  <>
                  {/* Telefono: cada item como tarjeta apilada (la tabla no entra) */}
                  <div className="space-y-2 sm:hidden">
                    {items.map((item, index) => (
                      <div key={`m-${item.productId || 'manual'}-${index}`} className="rounded-xl border bg-card p-3 space-y-2">
                        <input
                          type="text"
                          value={item.name || ''}
                          onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                          className="w-full px-3 py-2 text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring bg-background border-border text-foreground"
                          placeholder="Nombre del articulo"
                        />
                        {esItemLibre(itemsVinculados[index] || item) && !['servicio', 'service'].includes(String(((itemsVinculados[index] || item) as any).type || '').toLowerCase()) && (
                          <p className="text-[11px] text-amber-600 dark:text-amber-400">
                            Sin vincular al catálogo: no descuenta stock. Buscalo arriba para vincularlo.
                          </p>
                        )}
                        {comboChecklist(item, index)}
                        {marcaDescuento(index)}
                        <div className="flex items-end gap-2">
                          <div className="w-16 shrink-0">
                            <p className="mb-1 text-[11px] text-muted-foreground">Cant.</p>
                            <NumberInput
                              min={1}
                              value={item.quantity || 1}
                              onChange={(v) => handleItemChange(index, 'quantity', v)}
                              className="w-full px-2 py-2 text-center text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring bg-background border-border text-foreground"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="mb-1 text-[11px] text-muted-foreground">Precio unit.</p>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unitPrice || 0}
                              onChange={(e) => {
                                const unitPrice = parseFloat(e.target.value) || 0;
                                const updated = [...items];
                                updated[index] = {
                                  ...updated[index],
                                  unitPrice,
                                  totalPrice: unitPrice * (updated[index].quantity || 1)
                                };
                                setItems(updated);
                                setValue('estimatedValue', updated.reduce((sum, it) => sum + (it.totalPrice || 0), 0));
                              }}
                              className="w-full px-2 py-2 text-right text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring bg-background border-border text-foreground"
                            />
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="mb-1 text-[11px] text-muted-foreground">Total</p>
                            <p className="py-2 text-base font-medium">{formatNumberAR((item.totalPrice || 0))}</p>
                          </div>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(index)} className="h-9 w-9 shrink-0 text-red-500 hover:text-red-600">
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Desktop: tabla clasica */}
                  <div className="hidden sm:block rounded-xl border shadow-sm overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Producto/Servicio</th>
                          <th className="px-3 py-3 text-center text-sm font-semibold text-foreground">Cant.</th>
                          <th className="px-3 py-3 text-right text-sm font-semibold text-foreground">Precio Unit.</th>
                          <th className="px-3 py-3 text-right text-sm font-semibold text-foreground">Total</th>
                          <th className="px-3 py-3 text-center text-sm font-semibold text-foreground">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="bg-card">
                        {items.map((item, index) => (
                          <tr
                            key={`${item.productId || 'manual'}-${index}`}
                            className="border-t border-border"
                            tabIndex={-1}
                            ref={(el) => { itemRowRefs.current[index] = el; }}
                            onClick={() => setFocusedItemIndex(index)}
                          >
                            <td className="px-4 py-3">
                              <input
                                type="text"
                                value={item.name || ''}
                                data-item-field="true"
                                data-item-col="name"
                                ref={(el) => { nameInputRefs.current[index] = el; }}
                                onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                                onFocus={() => setFocusedItemIndex(index)}
                                className="w-full px-3 py-2 text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-background border-border text-foreground"
                                placeholder="Nombre del articulo"
                              />
                              {/* Un producto escrito a mano no esta atado al catalogo:
                                  al emitir NO descuenta stock ni deja costo para el margen. */}
                              {esItemLibre(itemsVinculados[index] || item) && !['servicio', 'service'].includes(String(((itemsVinculados[index] || item) as any).type || '').toLowerCase()) && (
                                <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                                  Sin vincular al catálogo: no descuenta stock. Buscalo arriba para vincularlo.
                                </p>
                              )}
                              {comboChecklist(item, index)}
                              {marcaDescuento(index)}
                            </td>
                            <td className="px-3 py-3">
                              <NumberInput
                                min={1}
                                value={item.quantity || 1}
                                data-item-field="true"
                                data-item-col="qty"
                                onChange={(v) => handleItemChange(index, 'quantity', v)}
                                onFocus={() => setFocusedItemIndex(index)}
                                ref={(el) => { qtyInputRefs.current[index] = el; }}
                                className="w-20 px-2 py-2 text-center text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-background border-border text-foreground"
                              />
                            </td>
                            <td className="px-3 py-3 text-right">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unitPrice || 0}
                                data-item-field="true"
                                data-item-col="price"
                                onFocus={() => setFocusedItemIndex(index)}
                                ref={(el) => { priceInputRefs.current[index] = el; }}
                                onChange={(e) => {
                                  const unitPrice = parseFloat(e.target.value) || 0;
                                  const updated = [...items];
                                  updated[index] = {
                                    ...updated[index],
                                    unitPrice,
                                    totalPrice: unitPrice * (updated[index].quantity || 1)
                                  };
                                  setItems(updated);
                                  setValue('estimatedValue', updated.reduce((sum, it) => sum + (it.totalPrice || 0), 0));
                                }}
                                className="w-28 px-2 py-2 text-right text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-background border-border text-foreground"
                              />
                            </td>
                            <td className="px-3 py-3 text-right text-base font-medium">{formatNumberAR((item.totalPrice || 0))}</td>
                            <td className="px-3 py-3 text-center">
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(index)} className="h-8 w-8 text-red-500 hover:text-red-600">
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}

                {renderTotales()}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => navigate('/remitos')} className="h-9 px-4">
                  Cancelar
                </Button>
                <Button type="submit" className="h-9 px-4 rounded-lg bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-white/90 text-white dark:text-black">
                  Crear Remito
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {!isCreatePage && (
      <>
      {remitoIdsFilter.length > 0 && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
          <span className="text-muted-foreground">
            Mostrando {remitoIdsFilter.length === 1 ? 'el remito' : `los ${remitoIdsFilter.length} remitos`} de la factura seleccionada.
          </span>
          <button type="button" onClick={() => navigate('/remitos')} className="shrink-0 text-primary hover:underline font-medium">
            Ver todos
          </button>
        </div>
      )}
      {/* Buscador */}
      <div className="relative">
        <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
        <Input
          type="text"
          placeholder="Buscar por código, cliente, patente..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground bg-background dark:bg-input/30 border-input flex min-w-0 rounded-lg border px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive h-8 w-full sm:w-[320px] lg:w-[520px] pl-8"
        />
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Estado</Label>
          <Select value={remitoFilter} onValueChange={setRemitoFilter}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="draft">Solo borradores</SelectItem>
              <SelectItem value="issued_not_invoiced">Emitidos no facturados</SelectItem>
              <SelectItem value="issued_not_paid">Emitidos no cobrados</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Forma de pago</Label>
          <Select value={paymentFilter} onValueChange={setPaymentFilter}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {paymentOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Desde</Label>
          <DatePicker
            value={dateFrom}
            onChange={setDateFrom}
            className="h-8"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Hasta</Label>
          <DatePicker
            value={dateTo}
            onChange={setDateTo}
            className="h-8"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Limpiar</Label>
          <Button
            type="button"
            variant="outline"
            className="h-8 w-full"
            onClick={() => {
              setRemitoFilter('all');
              setPaymentFilter('all');
              setDateFrom('');
              setDateTo('');
            }}
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Botones de acción en lote */}
      {selectedRemitos.size > 0 && (
        <Card>
          <CardContent className="p-4 md:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap">
                <span className="text-sm font-medium text-foreground">
                  {selectedRemitos.size} remito{selectedRemitos.size !== 1 ? 's' : ''} seleccionado{selectedRemitos.size !== 1 ? 's' : ''}
                </span>
                {(() => {
                  const nonDraftCount = filteredRemitos.filter(remito => 
                    selectedRemitos.has(remito.id) && remito.status !== 'draft'
                  ).length;
                  if (nonDraftCount > 0) {
                    return (
                      <span className="text-sm text-orange-600 dark:text-orange-400">
                        ({nonDraftCount} no se pueden eliminar)
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <Button
                  onClick={() => handleBulkAction('issue')}
                  disabled={bulkActionLoading !== null}
                  className="h-9 px-4"
                  variant="outline"
                >
                  {bulkActionLoading === 'issue' ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      Emitiendo...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      Emitir Seleccionados
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleBulkAction('print')}
                  disabled={bulkActionLoading !== null}
                  className="h-9 px-4"
                  variant="outline"
                >
                  {bulkActionLoading === 'print' ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      Generando...
                    </>
                  ) : (
                    <>
                      <PrinterIcon className="h-4 w-4" />
                      Imprimir Seleccionados
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleBulkAction('delete')}
                  disabled={bulkActionLoading !== null}
                  className="h-9 px-4 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                  variant="outline"
                >
                  {bulkActionLoading === 'delete' ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      Eliminar Seleccionados
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de Remitos */}
      <Card className="border-0 shadow-none md:border md:shadow-sm">
        <CardContent className="p-0">
          {filteredRemitos.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hay remitos que coincidan con los filtros</p>
            </div>
          ) : (
            <>
              <div className="hidden md:block relative w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-muted/50 border-b">
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">
                        <Tip label="Seleccionar todos los remitos en borrador"><input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        /></Tip>
                      </TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Remito</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Cliente</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Tipo</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                      <TableHead className="hidden 2xl:table-cell text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Antiguedad</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Pago</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Valor</TableHead>
                      <TableHead className="text-foreground h-8 px-2 text-left align-middle font-medium whitespace-nowrap"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedRemitos.map((remito) => {
                      const isSelected = selectedRemitos.has(remito.id);
                      const ageDays = getRemitoAgeDays(remito.createdAt);
                      return (
                        <TableRow 
                          key={remito.id}
                          className={`border-b hover:bg-muted/50 cursor-pointer ${isSelected ? 'bg-muted' : ''}`}
                        >
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                            <Tip label={
                                userRole === 'admin'
                                  ? 'Seleccionar para eliminar (Admin puede eliminar cualquier remito)'
                                  : remito.status === 'draft'
                                    ? 'Seleccionar para eliminar'
                                    : 'Solo se pueden eliminar remitos en borrador'
                              }><input
                              type="checkbox"
                              checked={selectedRemitos.has(remito.id)}
                              onChange={() => handleSelectRemito(remito.id)}
                              className={`h-4 w-4 rounded border-gray-300 ${
                                userRole === 'admin' || remito.status === 'draft'
                                  ? 'text-blue-600 focus:ring-blue-500'
                                  : 'text-gray-400 cursor-not-allowed'
                              }`}
                              disabled={userRole !== 'admin' && remito.status !== 'draft'}
                              onClick={(e) => e.stopPropagation()}
                            /></Tip>
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                            <div className="font-medium">{remito.remitNumber}</div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(remito.createdAt).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                            </div>
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle text-left max-w-[140px] cq-clamp-wide">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="font-medium truncate cursor-default">
                                  {remito.clientIsPlaceholder && remito.licensePlate ? `Sin datos · ${remito.licensePlate}` : remito.clientName}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top">{remito.clientName}</TooltipContent>
                            </Tooltip>
                            {remito.clientIsPlaceholder && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); completarDatosCliente(remito); }}
                                className="text-xs text-amber-600 hover:text-amber-500 underline underline-offset-2"
                              >
                                Completar datos
                              </button>
                            )}
                            {remito.vehicleInfo && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="text-sm text-muted-foreground truncate cursor-default">
                                    {remito.licensePlate} - {remito.vehicleInfo}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">{remito.licensePlate} - {remito.vehicleInfo}</TooltipContent>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                            {(() => {
                              const its = Array.isArray(remito.items) ? remito.items : [];
                              const badge = getTypeBadge(remito.type);
                              if (!its.length) return badge;
                              return (
                                <Tooltip>
                                  <TooltipTrigger asChild><span className="cursor-help">{badge}</span></TooltipTrigger>
                                  <TooltipContent side="right" className="max-w-[300px]">
                                    <ul className="text-xs space-y-0.5 list-disc pl-4">
                                      {its.slice(0, 12).map((it: any, i: number) => (
                                        <li key={i}>{Number(it.quantity) > 1 ? `${it.quantity}x ` : ''}{it.name || it.description || 'Ítem'}</li>
                                      ))}
                                      {its.length > 12 ? <li>… y {its.length - 12} más</li> : null}
                                    </ul>
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                            {getStatusBadge(remito.status)}
                          </TableCell>
                          <TableCell className="hidden 2xl:table-cell py-1 px-2 align-middle whitespace-nowrap text-left">
                            <Badge className={getRemitoAgeBadgeClass(ageDays)}>{getRemitoAgeLabel(ageDays)}</Badge>
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                              {remito.paymentMethod ? (
                                <Tip label={describirPago(remito)}>
                                  <Badge variant="secondary">
                                    {remito.paymentMethod === 'efectivo' ? 'Efectivo' :
                                     remito.paymentMethod === 'debito' ? 'Débito' :
                                     remito.paymentMethod === 'transferencia' ? 'Transf.' :
                                     remito.paymentMethod === 'cheque' ? 'Cheque' :
                                     remito.paymentMethod === 'tarjeta_3' ? 'T3 (+15%)' :
                                     remito.paymentMethod === 'tarjeta_6' ? 'T6 (+24%)' :
                                     remito.paymentMethod === 'cuenta_corriente' ? 'Cta Cte' :
                                     remito.paymentMethod === METODO_MIXTO ? 'Mixto' :
                                     payMethodName(remito.paymentMethod)}
                                  </Badge>
                                </Tip>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                            {(() => {
                              const itemsSum = Array.isArray(remito.items)
                                ? remito.items.reduce((s: number, it: any) => s + Number(it.totalPrice || 0), 0)
                                : 0;
                              const subtotal = itemsSum > 0 ? itemsSum : Number(remito.estimatedValue || 0);
                              let discountAmount = Number(remito.discountAmount || 0);
                              let baseConDescuento = Math.max(0, subtotal - discountAmount);
                              const totalFinal = Number(remito.totalContado) > 0
                                ? Number(remito.totalContado)
                                : baseConDescuento;
                              let surcharge = totalFinal - baseConDescuento;
                              // Si hay descuento de cliente aplicado (appliedDiscount) y discountAmount está en 0
                              // pero totalContado es menor al subtotal, atribuir esa diferencia al descuento del cliente
                              // (evita mostrarlo como "descuento de medio de pago" azul)
                              if (remito.appliedDiscount && discountAmount === 0 && surcharge < -0.01) {
                                discountAmount = -surcharge;
                                baseConDescuento = Math.max(0, subtotal - discountAmount);
                                surcharge = totalFinal - baseConDescuento;
                              }
                              const hasAnyAdjustment = discountAmount > 0 || Math.abs(surcharge) > 0.01;

                              // Datos del descuento (nombre, %)
                              let discountLabel = '';
                              if (discountAmount > 0) {
                                try {
                                  const disc = typeof remito.appliedDiscount === 'string'
                                    ? JSON.parse(remito.appliedDiscount)
                                    : remito.appliedDiscount;
                                  if (disc) {
                                    discountLabel = disc.name || 'Descuento';
                                    if (disc.discount_type === 'percentage') discountLabel += ` (${disc.discount_value}%)`;
                                  }
                                } catch {}
                                if (!discountLabel) discountLabel = 'Descuento';
                              }
                              // Label del medio de pago
                              const pmLabel = remito.paymentMethod
                                ? (esMixto(remito) ? describirPago(remito) : getPaymentMethodLabel(remito.paymentMethod))
                                : '';
                              const pmPctSign = baseConDescuento > 0
                                ? ((surcharge / baseConDescuento) * 100)
                                : 0;

                              const cellContent = (
                                <div className="flex flex-col leading-tight cursor-help">
                                  <span className="font-semibold text-foreground">{formatCurrencyAR(totalFinal)}</span>
                                  {hasAnyAdjustment && (
                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                      {discountAmount > 0 && (
                                        <span className="text-[10px] px-1 py-0 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                          −{formatCurrencyAR(discountAmount)}
                                        </span>
                                      )}
                                      {surcharge > 0.01 && (
                                        <span className="text-[10px] px-1 py-0 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                          +{formatCurrencyAR(surcharge)}
                                        </span>
                                      )}
                                      {surcharge < -0.01 && (
                                        <span className="text-[10px] px-1 py-0 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                          {formatCurrencyAR(surcharge)}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );

                              if (!hasAnyAdjustment) {
                                return cellContent;
                              }

                              return (
                                <TooltipProvider delayDuration={150}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>{cellContent}</TooltipTrigger>
                                    <TooltipContent side="top" align="start" className="text-xs">
                                      <div className="min-w-[220px] space-y-1">
                                        <div className="flex justify-between gap-3">
                                          <span className="text-muted-foreground">Subtotal items:</span>
                                          <span className="tabular-nums">{formatCurrencyAR(subtotal)}</span>
                                        </div>
                                        {discountAmount > 0 && (
                                          <div className="flex justify-between gap-3 text-green-500">
                                            <span>{discountLabel}:</span>
                                            <span className="tabular-nums">−{formatCurrencyAR(discountAmount)}</span>
                                          </div>
                                        )}
                                        {discountAmount > 0 && Math.abs(surcharge) > 0.01 && (
                                          <div className="flex justify-between gap-3 border-t border-border pt-1">
                                            <span className="text-muted-foreground">Subtotal con descuento:</span>
                                            <span className="tabular-nums">{formatCurrencyAR(baseConDescuento)}</span>
                                          </div>
                                        )}
                                        {surcharge > 0.01 && (
                                          <div className="flex justify-between gap-3 text-amber-500">
                                            <span>Recargo {pmLabel} ({pmPctSign > 0 ? `+${pmPctSign.toFixed(1)}%` : ''}):</span>
                                            <span className="tabular-nums">+{formatCurrencyAR(surcharge)}</span>
                                          </div>
                                        )}
                                        {surcharge < -0.01 && (
                                          <div className="flex justify-between gap-3 text-blue-500">
                                            <span>Descuento {pmLabel}:</span>
                                            <span className="tabular-nums">{formatCurrencyAR(surcharge)}</span>
                                          </div>
                                        )}
                                        <div className="flex justify-between gap-3 border-t border-border pt-1 font-semibold">
                                          <span>Total final:</span>
                                          <span className="tabular-nums">{formatCurrencyAR(totalFinal)}</span>
                                        </div>
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end">
                              <ButtonGroup>
                                <Tip label="Ver detalles"><Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleView(remito);
                                  }}
                                  className="size-8"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </Button></Tip>
                                {remito.status === 'draft' && (
                                  <>
                                    <Tip label="Editar"><Button
                                      variant="outline"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(remito);
                                      }}
                                      className="size-8"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </Button></Tip>
                                    <Tip label="Emitir"><Button
                                      variant="outline"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleIssue(remito.id);
                                      }}
                                      disabled={processingActions.has(`issue-${remito.id}`)}
                                      className="size-8"
                                    >
                                      <CheckIcon className="h-4 w-4" />
                                    </Button></Tip>
                                    <Tip label="Eliminar"><Button
                                      variant="outline"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(remito.id);
                                      }}
                                      className="size-8"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </Button></Tip>
                                  </>
                                )}
                                {remito.status === 'issued' && (
                                  <>
                                    <Tip label="Cancelar"><Button
                                      variant="outline"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancel(remito.id);
                                      }}
                                      disabled={processingActions.has(`cancel-${remito.id}`)}
                                      className="size-8"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </Button></Tip>
                                    <Tip label="Descargar PDF"><Button
                                      variant="outline"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadPDF(remito.id);
                                      }}
                                      className="size-8"
                                    >
                                      <PrinterIcon className="h-4 w-4" />
                                    </Button></Tip>
                                    <Tip label={(remito as any).whatsappSent ? 'WhatsApp ya enviado' : 'Enviar por WhatsApp'}><Button
                                      variant="outline"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSendWhatsApp(remito);
                                      }}
                                      disabled={processingActions.has(`whatsapp-${remito.id}`)}
                                      className="size-8"
                                    >
                                      <WhatsAppIcon className="h-4 w-4" />
                                    </Button></Tip>
                                    <Tip label={remito.emailSent ? 'Email ya enviado' : 'Enviar por email'}><Button
                                      variant="outline"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSendEmail(remito);
                                      }}
                                      className="size-8"
                                    >
                                      <EnvelopeIcon className="h-4 w-4" />
                                    </Button></Tip>
                                    {!remito.invoicedAt && (
                                      <Tip label="Facturar remito"><Button
                                        variant="outline"
                                        size="icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleInvoice(remito);
                                        }}
                                        className="size-8 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                                      >
                                        <DocumentDuplicateIcon className="h-4 w-4" />
                                      </Button></Tip>
                                    )}
                                    {remito.invoicedAt && (
                                      <Tip label="Ver factura PDF"><Button
                                        variant="outline"
                                        size="icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewInvoicePdf(remito);
                                        }}
                                        className="size-8 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                                      >
                                        <span className="text-xs font-bold">F</span>
                                      </Button></Tip>
                                    )}
                                  </>
                                )}
                                {remito.status !== 'issued' && remito.status !== 'cancelled' && (
                                  <Tip label="Seleccionar medio de pago"><Button
                                    variant="outline"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePaymentMethod(remito);
                                    }}
                                    disabled={processingActions.has(`payment-${remito.id}`)}
                                    className="size-8"
                                  >
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                    </svg>
                                  </Button></Tip>
                                )}
                                {remito.status === 'cancelled' && (
                                  <Tip label="Reabrir remito (vuelve a Borrador para editar)"><Button
                                    variant="outline"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      requestReopen(remito);
                                    }}
                                    disabled={processingActions.has(`reopen-${remito.id}`)}
                                    className="size-8 text-blue-600 hover:text-blue-700"
                                  >
                                    <ArrowPathIcon className="h-4 w-4" />
                                  </Button></Tip>
                                )}
                              </ButtonGroup>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="md:hidden space-y-3 p-4">
                {paginatedRemitos.map((remito) => (
                  <div key={remito.id} className="rounded-xl border bg-card/80 p-4 shadow-sm space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedRemitos.has(remito.id)}
                          onChange={() => handleSelectRemito(remito.id)}
                          className={`mt-1 h-4 w-4 rounded border-gray-300 ${
                            userRole === 'admin' || remito.status === 'draft'
                              ? 'text-blue-600 focus:ring-blue-500' 
                              : 'text-gray-400 cursor-not-allowed'
                          }`}
                          disabled={userRole !== 'admin' && remito.status !== 'draft'}
                        />
                        <div>
                          <p className="text-xs text-muted-foreground">Remito</p>
                          <p className="text-base font-semibold">{remito.remitNumber}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(remito.createdAt).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(remito.status)}
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs uppercase text-muted-foreground">Cliente</p>
                        <p className="font-medium break-words">
                          {remito.clientIsPlaceholder && remito.licensePlate ? `Sin datos · ${remito.licensePlate}` : remito.clientName}
                        </p>
                        {remito.clientIsPlaceholder && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); completarDatosCliente(remito); }}
                            className="text-xs text-amber-600 hover:text-amber-500 underline underline-offset-2"
                          >
                            Completar datos
                          </button>
                        )}
                      </div>
                      <div>
                        <p className="text-xs uppercase text-muted-foreground">Valor</p>
                        <p className="font-semibold">{formatCurrencyAR(remito.totalContado || remito.estimatedValue || 0)}</p>
                      </div>
                      {remito.vehicleInfo && (
                        <div className="col-span-2">
                          <p className="text-xs uppercase text-muted-foreground">Vehículo</p>
                          <p className="text-muted-foreground">{remito.licensePlate} - {remito.vehicleInfo}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs uppercase text-muted-foreground">Tipo</p>
                        <div className="mt-1">{getTypeBadge(remito.type)}</div>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-muted-foreground">Antiguedad</p>
                        <Badge className={`mt-1 ${getRemitoAgeBadgeClass(getRemitoAgeDays(remito.createdAt))}`}>
                          {getRemitoAgeLabel(getRemitoAgeDays(remito.createdAt))}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-muted-foreground">Pago</p>
                        {remito.paymentMethod ? (
                          <Tip label={describirPago(remito)}>
                            <Badge variant="secondary" className="mt-1">
                              {remito.paymentMethod === 'efectivo' ? 'Efectivo' :
                               remito.paymentMethod === 'debito' ? 'Débito' :
                               remito.paymentMethod === 'transferencia' ? 'Transf.' :
                               remito.paymentMethod === 'cheque' ? 'Cheque' :
                               remito.paymentMethod === 'tarjeta_3' ? 'T3 (+15%)' :
                               remito.paymentMethod === 'tarjeta_6' ? 'T6 (+24%)' :
                               remito.paymentMethod === 'cuenta_corriente' ? 'Cta Cte' :
                               remito.paymentMethod === METODO_MIXTO ? 'Mixto' :
                               payMethodName(remito.paymentMethod)}
                            </Badge>
                          </Tip>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 min-w-[120px] justify-center"
                        onClick={() => handleView(remito)}
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        Ver
                      </Button>
                      {remito.status === 'draft' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 min-w-[120px] justify-center"
                            onClick={() => handleEdit(remito)}
                          >
                            <PencilIcon className="h-4 w-4 mr-1" />
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 min-w-[120px] justify-center"
                            disabled={processingActions.has(`issue-${remito.id}`)}
                            onClick={() => handleIssue(remito.id)}
                          >
                            <CheckIcon className="h-4 w-4 mr-1" />
                            Emitir
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 min-w-[120px] justify-center text-destructive"
                            onClick={() => handleDelete(remito.id)}
                          >
                            <TrashIcon className="h-4 w-4 mr-1" />
                            Eliminar
                          </Button>
                        </>
                      )}
                      {remito.status === 'issued' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 min-w-[120px] justify-center"
                            disabled={processingActions.has(`cancel-${remito.id}`)}
                            onClick={() => handleCancel(remito.id)}
                          >
                            <XMarkIcon className="h-4 w-4 mr-1" />
                            Cancelar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 min-w-[120px] justify-center"
                            onClick={() => handleDownloadPDF(remito.id)}
                          >
                            <PrinterIcon className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 min-w-[120px] justify-center"
                            disabled={processingActions.has(`whatsapp-${remito.id}`)}
                            onClick={() => handleSendWhatsApp(remito)}
                          >
                            <WhatsAppIcon className="h-4 w-4 mr-1" />
                            WhatsApp
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 min-w-[120px] justify-center"
                            onClick={() => handleSendEmail(remito)}
                          >
                            <EnvelopeIcon className="h-4 w-4 mr-1" />
                            Email
                          </Button>
                          {!remito.invoicedAt && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 min-w-[120px] justify-center text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                              onClick={() => handleInvoice(remito)}
                            >
                              <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                              Facturar
                            </Button>
                          )}
                          {remito.invoicedAt && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 min-w-[120px] justify-center text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                              onClick={() => handleViewInvoicePdf(remito)}
                            >
                              <PrinterIcon className="h-4 w-4 mr-1" />
                              Ver Factura
                            </Button>
                          )}
                        </>
                      )}
                      {remito.status !== 'issued' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 min-w-[120px] justify-center"
                          disabled={processingActions.has(`payment-${remito.id}`)}
                          onClick={() => handlePaymentMethod(remito)}
                        >
                          <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          Pago
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Paginación */}
      {!loading && filteredRemitos.length > 0 && (
        <div className="flex items-center justify-between px-2">
          <div className="hidden flex-1 text-muted-foreground text-sm md:block">
            Mostrando {startResult} de {totalResults} resultados
          </div>
          <div className="flex items-center space-x-6 lg:space-x-8">
            <div className="flex w-[100px] items-center justify-center font-medium text-sm">
              Página {currentPage} de {totalPages}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="icon"
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <span className="sr-only">Ir a la primera página</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m11 17-5-5 5-5"></path>
                <path d="m18 17-5-5 5-5"></path>
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <span className="sr-only">Ir a la página anterior</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <span className="sr-only">Ir a la página siguiente</span>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <span className="sr-only">Ir a la última página</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m6 17 5-5-5-5"></path>
                <path d="m13 17 5-5-5-5"></path>
              </svg>
            </Button>
          </div>
        </div>
      )}
      </>
      )}

      <VehicleCreateDialog
        open={vehicleModalOpen}
        onOpenChange={setVehicleModalOpen}
        clientId={selectedClientId}
        initialPlate={vehiclePrefillPlate}
        onCreated={handleVehicleCreated}
      />

      {/* Modal de Crear/Editar */}
      <Dialog open={showModal} onOpenChange={(open) => {
        if (!open) {
          handleCloseModal();
        }
      }}>
        <DialogContent className="max-w-4xl rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">
                {editingRemito ? 'Editar Remito' : 'Nuevo Remito'}
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-140px)] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            <form id="remito-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* Primera fila: Cliente y Vehículo */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clientId" className="font-medium text-sm">Cliente *</Label>
                  <ClientCombobox
                    clients={clients || []}
                    selectedClient={selectedClient || null}
                    onSelect={(client) => {
                      if (client) {
                        setValue('clientId', client.id, { shouldValidate: true });
                        setValue('vehicleId', ''); // Limpiar vehículo cuando cambia el cliente
                        handleClientChange(client.id);
                      } else {
                        setValue('clientId', '');
                        setValue('vehicleId', '');
                      }
                    }}
                    placeholder="Buscar cliente..."
                    getClientName={getClientName}
                    onCreateClient={(query) => {
                      const params = new URLSearchParams();
                      params.set('returnTo', `${location.pathname}${location.search || ''}`);
                      if (query?.trim()) params.set('name', query.trim());
                      navigate(`/clients/new?${params.toString()}`);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => { setValue('clientId', 'consumidor-final', { shouldValidate: true }); setValue('vehicleId', ''); handleClientChange('consumidor-final'); }}
                    className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    + Consumidor final (sin registrar)
                  </button>
                  <input
                    type="hidden"
                    {...register('clientId', { required: 'Cliente es requerido' })}
                  />
                  {errors.clientId && <p className="text-sm text-red-600 mt-1">{errors.clientId.message?.toString()}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicleId" className="font-medium text-sm">Vehículo</Label>
                  <Controller
                    name="vehicleId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value || undefined}
                        onValueChange={(value) => {
                          if (value === '__create__') { setVehiclePrefillPlate(''); setVehicleModalOpen(true); return; }
                          field.onChange(value || '');
                        }}
                        disabled={!selectedClientId}
                      >
                        <SelectTrigger className="w-full rounded-lg h-auto px-4 py-3">
                          <SelectValue placeholder="Sin vehículo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__create__">+ Crear vehículo...</SelectItem>
                          {clientVehicles.map((vehicle: any) => (
                            <SelectItem key={vehicle.id} value={vehicle.id}>
                              {getVehicleInfo(vehicle)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {/* Km del vehículo: cada documento que lo capture actualiza la
                      ficha del auto si es mayor (la ficha nunca retrocede). */}
                  {watch('vehicleId') && (
                    <div className="pt-1">
                      <Label htmlFor="mileage" className="text-xs text-muted-foreground">Km del vehículo</Label>
                      <div className="mt-1 flex gap-1.5">
                        <Input
                          id="mileage"
                          type="number"
                          min={0}
                          placeholder={fichaKmSeleccionado > 0 ? `Último: ${fichaKmSeleccionado.toLocaleString('es-AR')} km` : 'Ej: 85000'}
                          className="h-9"
                          {...register('mileage')}
                        />
                        <OdometerScanButton onKm={(km) => setValue('mileage', String(km))} kmReferencia={fichaKmSeleccionado || undefined} className="h-9 shrink-0" />
                      </div>
                      {kmMenorQueFicha && (
                        <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
                          La ficha tiene {fichaKmSeleccionado.toLocaleString('es-AR')} km. Si es un error de tipeo, corregilo — la ficha del vehículo no retrocede.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Presupuesto Relacionado y Cita Relacionada */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="relatedBudgetId" className="font-medium text-sm">
                    Presupuesto Relacionado (Opcional)
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Select
                          value={watch('relatedBudgetId') || undefined}
                          onValueChange={(value) => setValue('relatedBudgetId', value || '')}
                        >
                          <SelectTrigger id="relatedBudgetId" className="w-full rounded-lg h-auto px-4 py-3">
                            <SelectValue placeholder="Sin presupuesto relacionado" />
                          </SelectTrigger>
                          <SelectContent>
                            {(budgets || [])
                              .filter(budget => budget.status === 'approved' || budget.status === 'completed')
                              .map((budget) => (
                                <SelectItem key={budget.id} value={budget.id}>
                                  {budget.budgetNumber || budget.id} - {budget.clientName} - ${(budget.totalAmount || 0).toFixed(2)}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Selecciona un presupuesto aprobado para auto-completar datos</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="relatedAppointmentId" className="font-medium text-sm">
                    Turno Relacionado (Opcional)
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <Select
                          value={watch('relatedAppointmentId') || undefined}
                          onValueChange={(value) => setValue('relatedAppointmentId', value || '')}
                        >
                          <SelectTrigger id="relatedAppointmentId" className="w-full rounded-lg h-auto px-4 py-3">
                            <SelectValue placeholder="Sin turno relacionado" />
                          </SelectTrigger>
                          <SelectContent>
                            {(appointments || [])
                              .filter(appointment => appointment.status === 'completed' || appointment.status === 'in_progress')
                              .map((appointment) => (
                                <SelectItem key={appointment.id} value={appointment.id}>
                                  {new Date(appointment.scheduledDate).toLocaleDateString('es-AR')} - {appointment.clientName} - {appointment.serviceType}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Selecciona un turno completado para auto-completar datos</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>

              {/* Tipo, Tipo de Servicio y Método de Pago */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="type" className="font-medium text-sm">
                      Tipo de Remito *
                    </Label>
                    <Select
                      value={watch('type') || ''}
                      onValueChange={(value) => setValue('type', value, { shouldValidate: true })}
                    >
                      <SelectTrigger id="type">
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="service">Servicio</SelectItem>
                        <SelectItem value="delivery">Entrega</SelectItem>
                        <SelectItem value="warranty">Garantía</SelectItem>
                        <SelectItem value="internal">Interno</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.type && (
                      <p className="text-sm text-red-600">{errors.type.message as string}</p>
                    )}
                  </div>

                  {/* Fecha del remito: editable para corregir cargas tardías */}
                  <div className="space-y-2">
                    <Label className="font-medium text-sm">Fecha del remito</Label>
                    <DatePickerUI
                      value={watch('createdAt') || ''}
                      onChange={(v) => setValue('createdAt', v)}
                      className="w-full"
                    />
                  </div>

                  {/* Observaciones y Descuentos */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowObservationsModal(true)}
                      className="rounded-xl justify-center text-xs h-8 px-2 bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-white/90 text-white dark:text-black border-black dark:border-white"
                    >
                      <span className="text-xs truncate">
                        {watch('observations') ? watch('observations') : 'Observaciones'}
                      </span>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDiscountModal(true)}
                      className="rounded-xl justify-center text-xs h-8 px-2 bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-white/90 text-white dark:text-black border-black dark:border-white"
                    >
                      <span className="text-xs truncate">
                        {appliedDiscount ? `${appliedDiscount.name} - ${appliedDiscount.discount_type === 'percentage' ? `${appliedDiscount.discount_value}%` : `$${appliedDiscount.discount_value}`}` : 'Descuento'}
                      </span>
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="paymentMethod" className="font-medium text-sm">
                      Método de Pago
                    </Label>
                    <Select
                      value={watch('paymentMethod') || undefined}
                      onValueChange={(value) => {
                        setSelectedPaymentMethod(value || '');
                        setSelectedPaymentInterval(null);
                        setValue('paymentInterval', '');
                        setValue('paymentMethod', value || '');
                        if (value === METODO_MIXTO) setPagosMixtos((prev) => (prev.length ? prev : lineasIniciales()));

                        // Recalcular precio si hay items
                        if (items.length > 0 && value !== METODO_MIXTO) {
                          const basePrice = items.reduce((total, item) => total + ((item.quantity || 0) * (item.unitPrice || 0)), 0);
                          if (basePrice > 0 && value) {
                            calculatePriceWithPaymentMethod(basePrice, value);
                          }
                        }
                      }}
                    >
                      <SelectTrigger id="paymentMethod">
                        <SelectValue placeholder="Sin método de pago" />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.paymentMethod && (
                      <p className="text-sm text-red-600">{errors.paymentMethod.message as string}</p>
                    )}
                    {watch('paymentMethod') === METODO_MIXTO && (
                      <PagoMixtoEditor
                        base={baseCobrable()}
                        lineas={pagosMixtos}
                        onChange={setPagosMixtos}
                        opciones={paymentOptions}
                        cuentas={financialAccounts}
                        ccIntervals={ccIntervalOptions}
                      />
                    )}
                    {['transferencia', 'debito', 'qr'].includes(watch('paymentMethod') || '') && financialAccounts.length > 0 && (
                      <div className="mt-2">
                        <Label className="font-medium text-sm">Cuenta que recibe</Label>
                        <Select value={watch('accountId') || undefined} onValueChange={(v) => setValue('accountId', v || '')}>
                          <SelectTrigger><SelectValue placeholder="¿A qué cuenta entró?" /></SelectTrigger>
                          <SelectContent>
                            {financialAccounts.filter((a) => a.kind !== 'efectivo').map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedPaymentMethod === 'cuenta_corriente' && (
                <div className="space-y-2">
                  <Label htmlFor="paymentInterval" className="font-medium text-sm">
                    Intervalo de Pago *
                  </Label>
                  <Select
                    value={watch('paymentInterval') || undefined}
                    onValueChange={(value) => {
                      const interval = value ? parseInt(value) : null;
                      setSelectedPaymentInterval(interval);
                      setValue('paymentInterval', value || '', {
                        shouldValidate: selectedPaymentMethod === 'cuenta_corriente'
                      });

                      // Recalcular precio si hay items
                      if (items.length > 0 && value) {
                        const basePrice = items.reduce((total, item) => total + ((item.quantity || 0) * (item.unitPrice || 0)), 0);
                        if (basePrice > 0 && selectedPaymentMethod) {
                          calculatePriceWithPaymentMethod(basePrice, selectedPaymentMethod, interval || undefined);
                        }
                      }
                    }}
                  >
                    <SelectTrigger id="paymentInterval">
                      <SelectValue placeholder="Seleccionar intervalo" />
                    </SelectTrigger>
                    <SelectContent>
                      {ccIntervalOptions.map((option) => (
                        <SelectItem key={option.value} value={String(option.value)}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.paymentInterval && (
                    <p className="text-sm text-red-600">{errors.paymentInterval.message as string}</p>
                  )}
                </div>
              )}


              {/* Productos y Servicios */}
              <div className="space-y-2">
                <Label className="font-medium text-sm">
                  Productos y Servicios
                </Label>
                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div key={index} className="flex gap-3 items-center p-3 border border-border rounded-lg bg-card">
                      {/* Nombre del producto/servicio (editable) */}
                      <div className="flex-1 relative">
                        <Input
                          type="text"
                          value={item.name || ''}
                          onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                          placeholder="Nombre del producto o servicio"
                          className="w-full"
                          list={`products-list-${index}`}
                        />
                        <datalist id={`products-list-${index}`}>
                          {products.map(product => (
                            <option key={product.id} value={product.name}>
                              {`$${(product.salePrice || 0).toFixed(2)}`}
                            </option>
                          ))}
                        </datalist>
                        {comboChecklist(item, index)}
                        {marcaDescuento(index)}
                      </div>

                      {/* Campo de cantidad */}
                      <div className="w-20">
                        <Input
                          type="number"
                          placeholder="Cant."
                          value={item.quantity || ''}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="1"
                          className="w-full"
                        />
                      </div>

                      {/* Precio unitario editable */}
                      <div className="w-28">
                        <Input
                          type="number"
                          placeholder="Precio"
                          value={item.unitPrice || 0}
                          min="0"
                          step="0.01"
                          onChange={(e) => {
                            const unitPrice = parseFloat(e.target.value) || 0;
                            const updated = [...items];
                            updated[index] = {
                              ...updated[index],
                              unitPrice,
                              totalPrice: unitPrice * (updated[index].quantity || 1)
                            };
                            setItems(updated);
                          }}
                          className="w-full text-right"
                        />
                      </div>

                      {/* Total */}
                      <div className="w-28 text-right">
                        <div className="px-3 py-2 rounded-lg text-sm font-medium bg-muted text-foreground">
                          {formatCurrencyAR((item.quantity || 0) * (item.unitPrice || 0))}
                        </div>
                      </div>

                      {/* Botón eliminar */}
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addItem}
                    className="w-full border-dashed"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Agregar Producto o Servicio
                  </Button>
                </div>
                
                {/* Total */}
                {renderTotales()}
              </div>

              {/* Botones */}
            </form>
          </div>
          <div className="px-6 py-4 border-t bg-muted/30 flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleCloseModal()}
              className="h-9 px-4"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              form="remito-form"
              className="h-9 px-4 rounded-lg bg-black hover:bg-black/90 text-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              {editingRemito ? 'Actualizar' : 'Crear'} Remito
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Ver Detalles */}
      {showViewModal && viewingRemito ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background text-foreground rounded-lg shadow-xl p-6 w-full max-w-4xl mx-4 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Remito {viewingRemito.remitNumber}
              </h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Información General */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 dark:text-white">Información General</h4>
                
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">Cliente:</label>
                  <p className="font-medium text-gray-900 dark:text-white">{viewingRemito.clientName}</p>
                  {viewingRemito.companyName && (
                    <p className="text-sm text-gray-600 dark:text-gray-300">{viewingRemito.companyName}</p>
                  )}
                </div>
                
                {/* Vehículo (editable, incluso si el remito ya está emitido) */}
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">Vehículo:</label>
                  {editingRemitoVehicle ? (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <Select value={remitoVehicleId || 'none'} onValueChange={(val) => setRemitoVehicleId(val === 'none' ? '' : val)}>
                        <SelectTrigger className="h-8 w-64"><SelectValue placeholder="Seleccionar vehículo" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin vehículo</SelectItem>
                          {viewClientVehicles.map((v: any) => (
                            <SelectItem key={v.id} value={String(v.id)}>
                              {(v.licensePlate ? `${v.licensePlate} - ` : '')}{v.make || v.brand || ''} {v.model || ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" className="h-8" onClick={saveRemitoVehicle} disabled={savingRemitoVehicle}>{savingRemitoVehicle ? 'Guardando...' : 'Guardar'}</Button>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => { setRemitoVehicleId((viewingRemito as any).vehicleId || ''); setEditingRemitoVehicle(false); }}>Cancelar</Button>
                    </div>
                  ) : (
                    <div className="mt-1 flex items-center gap-2">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {viewingRemito.vehicleInfo ? `${viewingRemito.licensePlate || ''} - ${viewingRemito.vehicleInfo}` : 'Sin vehículo'}
                      </p>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => setEditingRemitoVehicle(true)}>Editar</Button>
                    </div>
                  )}
                  {editingRemitoVehicle && viewClientVehicles.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">El cliente no tiene vehículos cargados.</p>
                  )}
                </div>

                {/* Km del vehículo (editable) */}
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">Km del vehículo:</label>
                  {editingRemitoKm ? (
                    <div className="mt-1 flex items-center gap-2">
                      <Input type="number" min={0} value={remitoKmInput} onChange={(e) => setRemitoKmInput(e.target.value)} placeholder="Ej: 85000" className="h-8 w-36" autoFocus />
                      <Button size="sm" className="h-8" onClick={saveRemitoKm} disabled={savingRemitoKm}>{savingRemitoKm ? 'Guardando...' : 'Guardar'}</Button>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => { const mi = (viewingRemito as any).mileage; setRemitoKmInput(mi != null && mi !== '' ? String(mi) : ''); setEditingRemitoKm(false); }}>Cancelar</Button>
                    </div>
                  ) : (
                    <div className="mt-1 flex items-center gap-2">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {(viewingRemito as any).mileage != null && (viewingRemito as any).mileage !== '' ? `${Number((viewingRemito as any).mileage).toLocaleString('es-AR')} km` : 'Sin cargar'}
                      </p>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => setEditingRemitoKm(true)}>Editar</Button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">Estado:</label>
                  <div className="mt-1">{getStatusBadge(viewingRemito.status)}</div>
                </div>
                
                
              </div>

              {/* Detalles del Remito */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 dark:text-white">Detalles</h4>

                {/* OT asociada (solo si el remito viene de un turno/OT) */}
                {(viewingRemito as any).appointmentCode && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400">OT asociada:</label>
                    <p className="font-medium dark:text-white break-all">{(viewingRemito as any).appointmentCode}</p>
                  </div>
                )}
                {viewingRemito.relatedBudgetId && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400">Presupuesto asociado:</label>
                    <p className="font-medium dark:text-white break-all">
                      {(() => {
                        const found = budgets.find((b) => b.id === viewingRemito.relatedBudgetId);
                        return found?.budgetNumber || viewingRemito.relatedBudgetId;
                      })()}
                    </p>
                  </div>
                )}
                
                {typeof viewingRemito.estimatedValue === 'number' && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400">Valor Estimado:</label>
                    <p className="font-medium text-green-600">
                      ${(Number(viewingRemito.estimatedValue ?? 0)).toFixed(2)}
                    </p>
                  </div>
                )}
                
                {viewingRemito.observations && 
                 viewingRemito.observations !== 'Remito generado automáticamente desde turno inmediato' && (
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400">Observaciones:</label>
                    <p className="text-gray-700 dark:text-gray-300">{viewingRemito.observations}</p>
                  </div>
                )}
                
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">Emitido por:</label>
                  <p className="font-medium dark:text-white">{viewingRemito.createdByName || 'Usuario del sistema'}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {new Date(viewingRemito.createdAt).toLocaleString('es-AR')}
                  </p>
                </div>
              </div>
            </div>

            {/* Items del Remito */}
            {viewingRemito.items && viewingRemito.items.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h4 className="font-medium text-gray-900 dark:text-white mb-4">Productos y Servicios</h4>
                
                {/* Tabla de items */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Cantidad
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Descripción
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Precio Unit.
                        </th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-background divide-y divide-border">
                      {(() => {
                        // Usar directamente los items del remito para la visualización
                        const baseItems = Array.isArray(viewingRemito.items) ? viewingRemito.items : [];

                        // 3) Separar nombres agrupados por coma sólo si vienen así (fallback)
                        const expanded: any[] = [];
                        baseItems.forEach((it: any) => {
                          if (it && esNombreAgrupado(it.name)) {
                            const names = partirNombres(it.name);
                            const quantity = Number(it.quantity || names.length || 1);
                            const total = Number(it.totalPrice || 0);
                            const unit = Number(it.unitPrice || (quantity ? total / quantity : 0));
                            // Repartir total de manera equitativa entre los nombres
                            const perItemTotal = total > 0 ? total / names.length : unit;
                            const perItemUnit = unit > 0 ? unit : (perItemTotal / 1);
                            names.forEach((n: string) => {
                              expanded.push({
                                ...it,
                                name: n,
                                quantity: 1,
                                unitPrice: perItemUnit,
                                totalPrice: perItemTotal,
                              });
                            });
                          } else {
                            expanded.push(it);
                          }
                        });

                        // 4) Normalizar cantidades y precios (unitario/total) y eliminar duplicados
                        const itemGroups = new Map<string, any[]>();
                        
                        expanded.forEach((it: any) => {
                          if (!it || !it.name) return;
                          
                          const qty = Number(it.quantity || 1);
                          const unit = Number(it.unitPrice || 0);
                          const total = Number(it.totalPrice || (unit * qty));
                          
                          // Usar solo el nombre como clave para agrupar
                          const key = it.name.trim().toLowerCase();
                          
                          if (!itemGroups.has(key)) {
                            itemGroups.set(key, []);
                          }
                          
                          itemGroups.get(key)!.push({
                            ...it,
                            quantity: qty,
                            unitPrice: unit || (qty ? total / qty : 0),
                            totalPrice: total || (unit * qty),
                          });
                        });

                        // Consolidar grupos de items
                        const finalItems: any[] = [];
                        itemGroups.forEach((items, key) => {
                          if (items.length === 1) {
                            // Si solo hay un item, agregarlo tal como está
                            finalItems.push(items[0]);
                          } else {
                            // Si hay múltiples items con el mismo nombre, consolidarlos
                            const consolidated = {
                              ...items[0],
                              quantity: items.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
                              unitPrice: items.reduce((sum, item) => sum + Number(item.unitPrice || 0), 0) / items.length,
                              totalPrice: items.reduce((sum, item) => sum + Number(item.totalPrice || 0), 0),
                            };
                            finalItems.push(consolidated);
                          }
                        });

                        // Qué renglones llevan el descuento. Solo se marca si el
                        // cálculo cierra con el monto guardado (en remitos viejos
                        // la regla era otra y la marca mentiría).
                        const nombresConDescuento = (() => {
                          const vacio = new Set<string>();
                          let disc: any = viewingRemito.appliedDiscount;
                          if (typeof disc === 'string') { try { disc = JSON.parse(disc); } catch { disc = null; } }
                          const guardado = Number(viewingRemito.discountAmount || 0);
                          if (!disc || guardado <= 0) return vacio;
                          const vinc = vincularConCatalogo((viewingRemito.items || []) as RemitoItem[], products);
                          const dentro = itemsEnDescuento(disc, vinc, (viewingRemito as any).clientId);
                          const base = dentro.reduce((s, it) => s + Number(it.totalPrice || 0), 0);
                          let calculado = disc.discount_type === 'percentage'
                            ? base * Number(disc.discount_value || 0) / 100
                            : Math.min(Number(disc.discount_value || 0), base);
                          if (Number(disc.max_discount) > 0) calculado = Math.min(calculado, Number(disc.max_discount));
                          if (Math.abs(calculado - guardado) > 1) return vacio;
                          return new Set(dentro.map((it) => String(it.name || '').trim().toLowerCase()));
                        })();

                        return finalItems.map((item, index) => (
                        <tr key={index} className="hover:bg-muted/50">
                            <td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                              {item.quantity}
                            </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">{item.name}</div>
                                {item.description && item.description !== item.name && (
                                <div className="text-sm text-gray-500 dark:text-gray-400">{item.description}</div>
                              )}
                              {nombresConDescuento.has(String(item.name || '').trim().toLowerCase()) && (
                                <div className="text-[11px] text-green-600 dark:text-green-400">Con descuento</div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                              {formatCurrencyAR(Number(item.unitPrice || 0))}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900 dark:text-white">
                              {formatCurrencyAR(Number(item.totalPrice || (Number(item.unitPrice||0) * Number(item.quantity||1))))}
                          </td>
                        </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

                {/* Resumen de precios */}
                <div className="mt-6 p-4 bg-muted/40 border border-border rounded-lg">
                  <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">💰 Resumen de Precios</h5>
                  
                  {/* Subtotal (antes de descuentos) */}
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-700 dark:text-gray-300">Subtotal:</span>
                    <span className="text-sm text-gray-900 dark:text-white">
                      {(() => {
                        const totalFromItems = viewingRemito.items?.reduce((sum, item) => {
                          return sum + Number(item.totalPrice || 0);
                        }, 0) || 0;
                        return formatCurrencyAR(totalFromItems);
                      })()}
                    </span>
                  </div>

                  {/* Descuento personalizado aplicado */}
                  {viewingRemito.appliedDiscount && viewingRemito.discountAmount && viewingRemito.discountAmount > 0 && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        🎯 Descuento {(() => {
                          // appliedDiscount ya debería estar parseado como objeto desde el backend
                          const discount = typeof viewingRemito.appliedDiscount === 'string' 
                            ? JSON.parse(viewingRemito.appliedDiscount) 
                            : viewingRemito.appliedDiscount;
                          
                          const discountName = discount.name || 'Descuento aplicado';
                          let scopeIndicator = '';
                          
                          const sinServicios = serviciosExcluidos(discount);
                          if (discount.applies_to === 'services') {
                            scopeIndicator = sinServicios.length
                              ? ` (Servicios, sin ${sinServicios.join(', ')})`
                              : ' (Servicios)';
                          } else if (discount.applies_to === 'products') {
                            scopeIndicator = ' (Productos)';
                          } else if (discount.applies_to === 'all') {
                            scopeIndicator = ' (Total)';
                          } else {
                            scopeIndicator = ' (Total)'; // Por defecto
                          }
                          
                          return discountName + scopeIndicator;
                        })()}:
                      </span>
                      <span className="text-sm text-red-600 dark:text-red-400 font-medium">
                        -{formatCurrencyAR(Number(viewingRemito.discountAmount))}
                      </span>
                    </div>
                  )}

                  {/* Cupón aplicado */}
                  {(() => {
                    if (!viewingRemito.appliedCoupon) return null;
                    try {
                      const couponData = typeof viewingRemito.appliedCoupon === 'string'
                        ? JSON.parse(viewingRemito.appliedCoupon)
                        : viewingRemito.appliedCoupon;
                      if (!couponData || !couponData.coupon) return null;
                      return (
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            🎟️ Cupón <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">{couponData.coupon.code}</span>
                            {couponData.coupon.discount_type === 'percentage'
                              ? ` (${couponData.coupon.discount_value}%)`
                              : ` ($${couponData.coupon.discount_value})`
                            }:
                          </span>
                          <span className="text-sm text-red-600 dark:text-red-400 font-medium">
                            -{formatCurrencyAR(Number(couponData.discount_amount || 0))}
                          </span>
                        </div>
                      );
                    } catch { return null; }
                  })()}

              {/* Total de Contado = precio de contado (subtotal − descuento), SIN el recargo del método.
                  Solo se muestra si difiere del subtotal (hay descuento); si es igual, no repetir el valor. */}
                  {(() => {
                    const subtotalFromItems = viewingRemito.items?.reduce((sum, item) => sum + Number(item.totalPrice || 0), 0) || 0;
                    const discountAmount = Number(viewingRemito.discountAmount || 0);
                    const contado = Math.max(0, subtotalFromItems - discountAmount);
                    if (contado === subtotalFromItems) return null; // igual al subtotal → no repetir
                    return (
                      <div className="flex justify-between items-center mb-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total de Contado:</span>
                        <span className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrencyAR(contado)}</span>
                      </div>
                    );
                  })()}

                  {/* Medio de pago y precio final (acá SÍ va el total con recargo del método) */}
                  {viewingRemito.paymentMethod && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      {esMixto(viewingRemito) && (viewingRemito.payments || []).length > 0 && (
                        <div className="mb-2 space-y-1">
                          {(viewingRemito.payments || []).map((p, i) => (
                            <div key={p.id || i} className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                              <span>
                                {payMethodName(p.method)}
                                {p.surcharge > 0 ? ` (${formatCurrencyAR(p.base)} + ${p.surchargePct}%)` : ''}
                              </span>
                              <span>{formatCurrencyAR(p.amount)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          💳 {getPaymentMethodLabel(viewingRemito.paymentMethod)}:
                        </span>
                        <span className="text-xl font-bold text-gray-900 dark:text-white">
                          {formatCurrencyAR(Number(viewingRemito.totalContado ?? viewingRemito.estimatedValue ?? 0))}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Puntos de fidelización */}
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      🎁 Con esta compra sumás{' '}
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {(() => {
                      // Calcular puntos basado en el total real
                      const totalForPoints = viewingRemito.paymentMethod && viewingRemito.totalContado 
                        ? viewingRemito.totalContado 
                        : viewingRemito.items?.reduce((sum, item) => sum + Number(item.totalPrice || 0), 0) || 0;
                      return Math.floor(totalForPoints / 1000);
                    })()} puntos
                      </span>{' '}
                      de fidelización (1 punto por cada $1.000 gastados)
                    </div>
                  </div>
                </div>

            {/* Fechas importantes y quién retiró */}
            {(viewingRemito.issuedAt || viewingRemito.deliveredAt || viewingRemito.status !== 'cancelled') && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h4 className="font-medium text-gray-900 dark:text-white mb-4">Fechas</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {viewingRemito.issuedAt && (
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Emitido:</label>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {new Date(viewingRemito.issuedAt).toLocaleString('es-AR')}
                      </p>
                    </div>
                  )}
                  
                  {viewingRemito.deliveredAt && (
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Entregado:</label>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {new Date(viewingRemito.deliveredAt).toLocaleString('es-AR')}
                      </p>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-sm text-gray-500 dark:text-gray-400">Retiró el vehículo:</label>
                    {viewingRemito.receivedBy ? (
                      <p className="font-medium text-gray-900 dark:text-white">
                        {viewingRemito.receivedBy}
                        {viewingRemito.receivedByDni && (
                          <span className="font-normal text-muted-foreground"> · DNI {String(viewingRemito.receivedByDni).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</span>
                        )}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">Sin cargar</p>
                    )}
                    {viewingRemito.status !== 'cancelled' && (
                      <button type="button" onClick={() => cargarQuienRetiro(viewingRemito)} className="mt-1 text-sm text-primary hover:underline">
                        {viewingRemito.receivedBy ? 'Corregir' : 'Cargar quién retiró'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Archivos adjuntos */}
            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  <PaperClipIcon className="h-4 w-4" /> Archivos adjuntos
                  {(viewingRemito.attachments || []).length > 0 && (
                    <Badge variant="secondary" className="text-xs">{(viewingRemito.attachments || []).length}</Badge>
                  )}
                </h4>
                <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer transition-colors">
                  <PlusIcon className="h-4 w-4" /> Adjuntar
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.xlsx,.xls,.csv,.doc,.docx,.txt,.zip"
                    onChange={async (e) => {
                      const files = e.target.files;
                      if (!files || files.length === 0) return;
                      const formData = new FormData();
                      for (let i = 0; i < files.length; i++) formData.append('files', files[i]);
                      try {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`/api/remitos/${viewingRemito.id}/attachments`, formData, {
                          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
                        });
                        toast.success(`${files.length} archivo${files.length > 1 ? 's' : ''} adjuntado${files.length > 1 ? 's' : ''}`);
                        // Actualizar el remito local con los nuevos attachments
                        setViewingRemito((prev: any) => prev ? { ...prev, attachments: res.data.attachments } : prev);
                        // Refrescar lista
                        fetchRemitos();
                      } catch (err: any) {
                        toast.error(err?.response?.data?.error || 'Error al subir archivo');
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              {(viewingRemito.attachments || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No hay archivos adjuntos. Podes subir OC, fotos, PDFs y mas.</p>
              ) : (
                <div className="space-y-2">
                  {(viewingRemito.attachments || []).map((att: any) => (
                    <div key={att.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-border bg-muted/30">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <PaperClipIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{att.originalName}</p>
                          <p className="text-xs text-muted-foreground">
                            {(att.size / 1024).toFixed(0)} KB — {att.uploadedBy || 'Sistema'} — {new Date(att.uploadedAt).toLocaleDateString('es-AR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Tip label="Descargar"><button
                          type="button"
                          className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const res = await axios.get(`/api/remitos/${viewingRemito.id}/attachments/${att.id}`, {
                                headers: { Authorization: `Bearer ${token}` },
                                responseType: 'blob'
                              });
                              const url = URL.createObjectURL(new Blob([res.data]));
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = att.originalName;
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch (err: any) {
                              toast.error('Error al descargar archivo');
                            }
                          }}
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                        </button></Tip>
                        <Tip label="Eliminar"><button
                          type="button"
                          className="p-1.5 rounded-md hover:bg-red-500/10 transition-colors text-muted-foreground hover:text-red-500"
                          onClick={() => setDeleteAttachmentTarget({
                            remitoId: viewingRemito.id,
                            attachmentId: att.id,
                            name: att.originalName
                          })}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button></Tip>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Botones de acción */}
            <div className="mt-6 pt-6 border-t border-border flex justify-end space-x-3">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 border border-border text-foreground rounded-md hover:bg-muted transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      ) : null}


      {/* Modal de Impresión de Remito */}
      {showPrintModal && printingRemito && (
        <RemitoPrintView
          remito={printingRemito}
          onClose={() => {
            setShowPrintModal(false);
            setPrintingRemito(null);
          }}
          processedItems={computeProcessedItems(printingRemito)}
        />
      )}

      {/* Modal: Confirmar reabrir remito */}
      <Dialog
        open={!!reopenTarget}
        onOpenChange={(open) => { if (!open) setReopenTarget(null); }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>¿Reabrir remito?</DialogTitle>
            <DialogDescription>
              El remito <strong className="text-foreground">{reopenTarget?.remitNumber}</strong> volverá al estado <strong className="text-foreground">Borrador</strong> para que puedas editarlo nuevamente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setReopenTarget(null)}>
              Cancelar
            </Button>
            <Button type="button" onClick={confirmReopen}>
              Sí, reabrir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Confirmar eliminar archivo adjunto */}
      <Dialog
        open={!!deleteAttachmentTarget}
        onOpenChange={(open) => { if (!open && !deletingAttachment) setDeleteAttachmentTarget(null); }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>¿Eliminar archivo?</DialogTitle>
            <DialogDescription>
              Se va a eliminar permanentemente el archivo <strong className="text-foreground">{deleteAttachmentTarget?.name}</strong> del remito. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setDeleteAttachmentTarget(null)} disabled={deletingAttachment}>
              Cancelar
            </Button>
            <Button type="button" variant="destructive" onClick={confirmDeleteAttachment} disabled={deletingAttachment}>
              {deletingAttachment ? 'Eliminando...' : 'Sí, eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Selección de Medio de Pago */}
      <Dialog
        open={showPaymentModal && !!selectedRemitoForPayment}
        onOpenChange={(open) => {
          if (!open) {
            setShowPaymentModal(false);
            setSelectedRemitoForPayment(null);
            setSelectedPaymentMethod('');
            setSelectedPaymentInterval(null);
            setModalCalculatedPrice(0);
            setModalPriceSurcharge(0);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Seleccionar Medio de Pago</DialogTitle>
            {selectedRemitoForPayment && (
              <DialogDescription>
                Remito: <span className="font-medium text-foreground">{selectedRemitoForPayment.remitNumber}</span>
                <br />
                Cliente: <span className="text-foreground">{selectedRemitoForPayment.clientName}</span>
              </DialogDescription>
            )}
          </DialogHeader>

          {selectedRemitoForPayment && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="payment-method-select">Método de Pago *</Label>
                <Select
                  value={selectedPaymentMethod || ''}
                  onValueChange={async (method) => {
                    setSelectedPaymentMethod(method);

                    if (method === 'cuenta_corriente') {
                      setSelectedPaymentInterval(30);
                    } else {
                      setSelectedPaymentInterval(null);
                    }

                    if (method === METODO_MIXTO) {
                      // Cobro mixto: el editor muestra el reparto; no hay precio único
                      setPagosMixtosModal((prev) => (prev.length ? prev : lineasDesdePagos(selectedRemitoForPayment?.payments)));
                      setModalCalculatedPrice(0);
                      setModalPriceSurcharge(0);
                    } else if (method && selectedRemitoForPayment) {
                      const basePrice = selectedRemitoForPayment.estimatedValue;
                      const newPrice = await calculatePriceWithPaymentMethod(
                        basePrice,
                        method,
                        method === 'cuenta_corriente' ? 30 : undefined
                      );
                      setModalCalculatedPrice(newPrice);
                      setModalPriceSurcharge(newPrice - basePrice);
                    } else {
                      setModalCalculatedPrice(0);
                      setModalPriceSurcharge(0);
                    }
                  }}
                >
                  <SelectTrigger id="payment-method-select">
                    <SelectValue placeholder="Seleccionar método de pago" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedPaymentMethod === METODO_MIXTO && (
                <PagoMixtoEditor
                  base={baseCobrableDe(selectedRemitoForPayment)}
                  lineas={pagosMixtosModal}
                  onChange={setPagosMixtosModal}
                  opciones={paymentOptions}
                  cuentas={financialAccounts}
                  ccIntervals={ccIntervalOptions}
                />
              )}

              {selectedPaymentMethod && selectedPaymentMethod !== METODO_MIXTO && modalCalculatedPrice > 0 && (
                <div className="rounded-md border border-border bg-muted/40 p-3 space-y-1">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Precio base:</span>
                    <span className="font-medium text-foreground">{formatCurrencyAR(selectedRemitoForPayment.estimatedValue)}</span>
                  </div>
                  {modalPriceSurcharge > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Recargo:</span>
                      <span className="font-medium text-destructive">+{formatCurrencyAR(modalPriceSurcharge)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 mt-1 border-t border-border">
                    <span className="text-sm font-bold text-foreground">Precio final:</span>
                    <span className="text-sm font-bold text-green-600 dark:text-green-400">{formatCurrencyAR(modalCalculatedPrice)}</span>
                  </div>
                </div>
              )}

              <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground space-y-1">
                <p className="font-semibold text-foreground">Recargos aplicables:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Efectivo, Débito, Transferencia: Sin recargo</li>
                  <li>Tarjeta 3 cuotas: +15%</li>
                  <li>Tarjeta 6 cuotas: +24%</li>
                  <li>Cuenta Corriente: Sin recargo (30 días)</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowPaymentModal(false);
                setSelectedRemitoForPayment(null);
                setSelectedPaymentMethod('');
                setSelectedPaymentInterval(null);
                setModalCalculatedPrice(0);
                setModalPriceSurcharge(0);
              }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={!selectedPaymentMethod}
              onClick={() => {
                if (selectedPaymentMethod) {
                  const interval = selectedPaymentMethod === 'cuenta_corriente' ? selectedPaymentInterval : undefined;
                  handleUpdatePaymentMethod(selectedPaymentMethod, interval || undefined);
                }
              }}
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmación de Facturación */}
      {showInvoiceModal && selectedRemitoForInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl p-6 w-full max-w-lg mx-4 transition-colors duration-200 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex justify-between items-center mb-6">
              <h3 className={`text-lg font-medium transition-colors duration-200 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Facturar Remito
              </h3>
              <button
                onClick={() => {
                  setShowInvoiceModal(false);
                  setSelectedRemitoForInvoice(null);
                }}
                className={`transition-colors duration-200 ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Remito:</span>
                  <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{selectedRemitoForInvoice.remitNumber}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Cliente:</span>
                  <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{selectedRemitoForInvoice.clientName}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Método de pago:</span>
                  <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                    {getPaymentMethodLabel(selectedRemitoForInvoice.paymentMethod || '')}
                  </span>
                </div>
                <div className={`flex justify-between items-center pt-2 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  <span className={`text-sm font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Total:</span>
                  <span className={`text-lg font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                    {formatCurrencyAR(selectedRemitoForInvoice.totalContado || selectedRemitoForInvoice.estimatedValue || 0)}
                  </span>
                </div>
              </div>

              {selectedRemitoForInvoice.items && selectedRemitoForInvoice.items.length > 0 && (
                <div>
                  <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Items a facturar ({selectedRemitoForInvoice.items.length}):
                  </h4>
                  <div className={`max-h-40 overflow-y-auto rounded-lg border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    {selectedRemitoForInvoice.items.map((item, index) => (
                      <div key={index} className={`flex justify-between items-center px-3 py-2 ${index % 2 === 0 ? (isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50') : ''}`}>
                        <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {item.quantity}x {item.name}
                        </span>
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                          {formatCurrencyAR(item.totalPrice)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50 border border-gray-600' : 'bg-gray-100 border border-gray-200'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Se creará una factura borrador con los datos del remito. Luego podrás autorizarla en AFIP desde la sección de Facturas.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowInvoiceModal(false);
                    setSelectedRemitoForInvoice(null);
                  }}
                  disabled={isCreatingInvoice}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={confirmInvoice}
                  disabled={isCreatingInvoice}
                >
                  {isCreatingInvoice ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creando factura...
                    </>
                  ) : (
                    <>
                      <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                      Crear Factura
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de observaciones */}
      <Dialog open={showObservationsModal} onOpenChange={setShowObservationsModal}>
        <DialogContent className="max-w-2xl rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Observaciones</DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Observaciones adicionales (opcional)</Label>
                <Textarea
                  id="observations"
                  {...register('observations')}
                  rows={8}
                  className="rounded-lg"
                  placeholder="Escribe las observaciones aquí..."
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-3 pt-6 px-6 pb-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowObservationsModal(false);
              }}
              className="rounded-xl"
            >
              Cerrar
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowObservationsModal(false);
              }}
              className="rounded-xl"
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de descuentos */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent className="max-w-2xl rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Seleccionar Descuento</DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="space-y-4">
              {/* Cupones de descuento */}
              <div className="space-y-2">
                <Label className="font-medium text-sm">Cupones de descuento</Label>
                <CouponInput
                  onCouponApplied={handleCouponApplied}
                  onCouponRemoved={handleCouponRemoved}
                  appliedCoupon={appliedCoupon}
                  totalAmount={items.reduce((total, item) => total + ((item.quantity || 0) * (item.unitPrice || 0)), 0)}
                  items={items.map(item => ({
                    type: item.type || 'service',
                    totalPrice: (item.quantity || 0) * (item.unitPrice || 0)
                  }))}
                  clientId={watch('clientId')}
                />
              </div>

              {/* Descuentos personalizados */}
              <div className="space-y-2">
                <Label className="font-medium text-sm">Descuentos personalizados</Label>
                {customDiscounts.filter(isDiscountSelectable).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="text-sm">No hay descuentos disponibles</p>
                  </div>
                ) : (
                  <Select
                    value={selectedDiscount || undefined}
                    onValueChange={(value) => {
                      handleDiscountChange(value || '');
                    }}
                  >
                    <SelectTrigger className="w-full rounded-lg">
                      <SelectValue placeholder="Seleccionar descuento" />
                    </SelectTrigger>
                    <SelectContent>
                      {customDiscounts
                        .filter(isDiscountSelectable)
                        .map(discount => (
                          <SelectItem key={discount.id} value={String(discount.id)}>
                            {discount.name} - {discount.discount_type === 'percentage'
                              ? `${discount.discount_value}%`
                              : `$${discount.discount_value}`}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                )}
                {appliedDiscount && (
                  <div className="mt-3 p-4 bg-primary/10 border border-primary/20 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-foreground">{appliedDiscount.name}</div>
                        <div className="text-sm text-muted-foreground mt-1">{appliedDiscount.description}</div>
                        {appliedDiscount.min_amount > 0 && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Monto mínimo: ${appliedDiscount.min_amount}
                          </div>
                        )}
                      </div>
                      <div className="text-lg font-semibold text-primary">
                        {appliedDiscount.discount_type === 'percentage'
                          ? `${appliedDiscount.discount_value}%`
                          : `$${appliedDiscount.discount_value}`}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-3 pt-6 px-6 pb-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDiscountModal(false);
              }}
              className="rounded-xl bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
            >
              Cerrar
            </Button>
            {appliedDiscount && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelectedDiscount('');
                  setAppliedDiscount(null);
                  setShowDiscountModal(false);
                }}
                className="rounded-xl"
              >
                Quitar descuento
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completar datos del cliente "por completar" (o vincularlo a un cliente
          ya cargado) — antes de cobrar o desde el link de la lista */}
      <ClientDataDialog
        open={!!cdClientId}
        onOpenChange={(o) => {
          if (!o) {
            setCdClientId(null);
            cdResolverRef.current?.(false);
            cdResolverRef.current = null;
          }
        }}
        clientId={cdClientId}
        onSaved={() => {
          cdResolverRef.current?.(true);
          cdResolverRef.current = null;
          // Refrescar: el nombre nuevo (o el cliente fusionado) tiene que verse
          // en la lista de remitos y en el selector de clientes
          fetchClients();
          fetchRemitosRef.current?.({ silent: true });
        }}
        skipLabel={!cdPostCobro ? 'El cliente se fue — cobrar sin completar (queda como "Sin datos" con su auto)' : undefined}
        onSkip={() => {
          setCdClientId(null);
          cdResolverRef.current?.(true);
          cdResolverRef.current = null;
        }}
      />
      {/* Falta el medio de pago para emitir: se pide acá y se emite en el mismo
          paso, en vez de mandar al usuario a editar el remito. */}
      <Dialog open={!!remitoSinMedio} onOpenChange={(o) => { if (!o) setRemitoSinMedio(null); }}>
        <DialogContent className={medioElegido === METODO_MIXTO ? 'max-w-lg' : 'max-w-sm'}>
          <DialogHeader>
            <DialogTitle>¿Cómo se cobra?</DialogTitle>
            <DialogDescription>
              El remito {remitoSinMedio?.remitNumber || ''} no tiene medio de pago. Sin eso no entra
              ni a la caja ni a la cuenta corriente del cliente.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {paymentOptions.map((opt: any) => (
              <Button
                key={opt.value}
                variant={medioElegido === opt.value ? 'default' : 'outline'}
                className="h-9 justify-start text-sm"
                onClick={() => {
                  setMedioElegido(opt.value);
                  if (opt.value === METODO_MIXTO) setPagosMixtosEmitir((prev) => (prev.length ? prev : lineasIniciales()));
                }}
              >
                {opt.label}
              </Button>
            ))}
          </div>
          {medioElegido === METODO_MIXTO && remitoSinMedio && (
            <PagoMixtoEditor
              base={baseCobrableDe(remitoSinMedio)}
              lineas={pagosMixtosEmitir}
              onChange={setPagosMixtosEmitir}
              opciones={paymentOptions}
              cuentas={financialAccounts}
              ccIntervals={ccIntervalOptions}
            />
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRemitoSinMedio(null)}>Cancelar</Button>
            <Button
              disabled={!medioElegido}
              onClick={() => {
                const r = remitoSinMedio;
                if (medioElegido === METODO_MIXTO) {
                  const reparto = repartirLineas(baseCobrableDe(r), pagosMixtosEmitir, recargosPorMetodo);
                  if (!reparto.ok) {
                    toast.error(reparto.error || 'Revisá el pago mixto');
                    return;
                  }
                  setRemitoSinMedio(null);
                  if (r) handleIssue(r.id, undefined, payloadPagos(pagosMixtosEmitir));
                  return;
                }
                setRemitoSinMedio(null);
                if (r) handleIssue(r.id, medioElegido);
              }}
            >
              Emitir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </ErrorBoundary>
  );
};

export default Remitos; 
